/**
 * HelperList Component
 * 
 * Displays a list of all registered helpers with the following features:
 * - Filter helpers by Fachbereich
 * - Export filtered list to Excel
 * - Delete all helpers (with confirmation) - Admin only
 * - Display helper details: name, email, Fachbereiche, registration date
 * 
 * @param onClose - Callback function to close the popup
 */
import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import axios from 'axios';
import * as XLSX from 'xlsx';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE_URL } from '../constants';
import { useFachbereiche } from '../contexts/FachbereicheContext';
import { useBackendStatus } from '../contexts/BackendStatusContext';
import RegistrationForm from './RegistrationForm';
import HelperListEmailModal from './HelperListEmailModal';
import HelperIdentityChangeModal from './HelperIdentityChangeModal';
import { convertBereitFuerMehrereEinsaetze, convertHelperBereitFuerMehrereEinsaetze, formatHelperDisplayName, hasEffectiveFachbereichZuordnung, helperMatchesSearch, isZentralBestaetigtesPasstNichtForFachbereich } from '../utils';
import './HelperList.css';
import './Switch.css';

interface Helferpunkt {
  einsatzdatum?: string;
  id: number;
  einsatzort: string;
  einsatzuhrzeit: string;
  einsatzuhrzeit_bis?: string | null;
  fachbereich: string;
}

interface HelperFeedback {
  fachbereich: string;
  rückmeldung: string | null;
  bemerkung: string | null;
  caretaker_id?: number | null;
  /** ISO timestamp when an Administrator confirmed central awareness of "Passt nicht" for this Fachbereich */
  passtNichtZentralBestaetigtAt?: string | null;
  passtNichtZentralBestaetigtVon?: number | null;
}

export interface Helper {
  id: number;
  first_name: string;
  surname: string;
  email: string | null;
  phone: string | null;
  fachbereiche: string[];
  feedback?: HelperFeedback[];
  wunscheinsatzort?: string;
  einsatzzeit?: string;
  bereit_fuer_mehrere_einsaetze?: string | boolean | null; // Backend returns boolean, frontend uses 'Ja'/'Nein'
  hilft_nicht_am_wettkampftag?: number | null; // 0 = helps on race day, 1 = doesn't help
  anmerkung?: string | null;
  aufbauhilfe?: string | null;
  kuchenspende?: number | null;
  tshirt?: string | null;
  created_at: string;
}

interface HelperListProps {
  onClose: () => void;
  initialFachbereich?: string;
  /** When true, initially show Kuchenspender (kuchenspende > 0) instead of Helfer */
  initialShowKuchenspender?: boolean;
  onEditFachbereich?: (fachbereich: string) => void;
  initialHelperToEdit?: Helper | null; // Helper to open in edit mode
  /** Badge counts for admin Gesamthelferliste - shown as focus banner when either > 0 */
  adminStatusCounts?: { noFachbereich: number; passtNicht: number };
  /** Called after edit/delete so parent can refresh status counts (e.g. badge) */
  onRefreshStatus?: () => void;
  /** When true, year is read-only (from App's maintenance status - ensures delete button visibility) */
  yearMaintenanceDisabled?: boolean;
}

// Sort Helferpunkt assignments by date (einsatzdatum) then by time (einsatzuhrzeit)
const sortAssignmentsByDateAndTime = (assignments: Helferpunkt[]): Helferpunkt[] => {
  return [...assignments].sort((a, b) => {
    const dateCompare = (a.einsatzdatum || '').localeCompare(b.einsatzdatum || '');
    if (dateCompare !== 0) return dateCompare;
    return (a.einsatzuhrzeit || '').localeCompare(b.einsatzuhrzeit || '');
  });
};

// Helper function to get German weekday abbreviation from date string (YYYY-MM-DD)
const getWeekdayAbbreviation = (dateString: string | undefined): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString + 'T00:00:00'); // Add time to avoid timezone issues
    const dayOfWeek = date.getDay();
    const abbreviations = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];
    return abbreviations[dayOfWeek] || '';
  } catch (e) {
    return '';
  }
};

// Helper function to format phone number columns as text in Excel to preserve leading zeros
const formatPhoneColumnAsText = (worksheet: XLSX.WorkSheet, columnName: string): void => {
  if (!worksheet || !worksheet['!ref']) return;
  
  const range = XLSX.utils.decode_range(worksheet['!ref']);
  if (!range || range.e.r < 0) return;
  
  // Find the column index for the phone column
  const headerRow = 0;
  let phoneColumnIndex = -1;
  
  // Get header row to find column index
  for (let col = range.s.c; col <= range.e.c; col++) {
    const cellRef = XLSX.utils.encode_cell({ r: headerRow, c: col });
    const cell = worksheet[cellRef];
    if (cell && (cell.v === columnName || cell.w === columnName)) {
      phoneColumnIndex = col;
      break;
    }
  }
  
  if (phoneColumnIndex === -1) return;
  
  // Format all cells in the phone column as text
  for (let row = range.s.r + 1; row <= range.e.r; row++) {
    const cellRef = XLSX.utils.encode_cell({ r: row, c: phoneColumnIndex });
    const cell = worksheet[cellRef];
    if (cell) {
      // Set cell type to string and format as text
      cell.t = 's'; // string type
      cell.z = '@'; // text format code in Excel
      // Ensure value is stored as string
      if (cell.v !== null && cell.v !== undefined) {
        cell.v = String(cell.v);
      }
    }
  }
};

const HelperList: React.FC<HelperListProps> = ({ onClose, initialFachbereich, initialShowKuchenspender, onEditFachbereich, initialHelperToEdit, adminStatusCounts, onRefreshStatus, yearMaintenanceDisabled: yearMaintenanceDisabledProp }) => {
  const { isAdmin, isViewer, canRegisterHelpers, canEmailVisibleHelpers, token, year, user } = useAuth();
  const { fachbereiche } = useFachbereiche();
  const { pauseHealthCheck, resumeHealthCheck } = useBackendStatus();
  const [helpers, setHelpers] = useState<Helper[]>([]);
  const [helperAssignments, setHelperAssignments] = useState<{ [key: number]: Helferpunkt[] }>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCleanupConfirm, setShowCleanupConfirm] = useState(false);
  const [cleaning, setCleaning] = useState(false);
  const [yearMaintenanceDisabledLocal, setYearMaintenanceDisabledLocal] = useState<boolean>(false);
  const yearMaintenanceDisabled = yearMaintenanceDisabledProp ?? yearMaintenanceDisabledLocal;
  const [showUnsavedEditConfirm, setShowUnsavedEditConfirm] = useState(false);
  const [registrationFormSelectedFachbereiche, setRegistrationFormSelectedFachbereiche] = useState<string[]>([]);
  const [originalEditFormData, setOriginalEditFormData] = useState<{
    first_name: string;
    surname: string;
    email: string;
    phone: string;
    wunscheinsatzort: string;
    einsatzzeit: string;
    bereit_fuer_mehrere_einsaetze: string;
    anmerkung: string;
    aufbauhilfe: string;
    kuchenspende: number;
    tshirt: string;
    fachbereiche: string[];
  } | null>(null);
  const [filterFachbereich, setFilterFachbereich] = useState<string>(initialFachbereich || '');
  const [searchQuery, setSearchQuery] = useState<string>('');
  const [showDuplicatesOnly, setShowDuplicatesOnly] = useState<boolean>(false);
  const [showUnassignedOnly, setShowUnassignedOnly] = useState<boolean>(false);
  const [showPasstNichtOnly, setShowPasstNichtOnly] = useState<boolean>(false);
  /** When true: include helpers whose only "Passt nicht" rows are already zentral bestätigt */
  const [showConfirmedPasstNicht, setShowConfirmedPasstNicht] = useState<boolean>(false);
  const [passtNichtBestaetigungSaving, setPasstNichtBestaetigungSaving] = useState<string | null>(null);
  const [showKuchenspender, setShowKuchenspender] = useState<boolean>(initialShowKuchenspender ?? false); // Default OFF - Kuchenspender hidden
  const [showOnlyReadyForMultiple, setShowOnlyReadyForMultiple] = useState<boolean>(false); // Default OFF - Einzel- und Mehrfacheinsätze (all); ON = Helfer bereit für mehr Einsätze
  const [showAdditionalHelperInfo, setShowAdditionalHelperInfo] = useState<boolean>(false); // Default OFF - Wunscheinsatzort, Einsatzzeit, Anmerkung, Aufbauhilfe hidden
  const [headerExpanded, setHeaderExpanded] = useState<boolean>(false); // Mobile: header collapsed by default
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);
  const [deleteConfirmId, setDeleteConfirmId] = useState<number | null>(null);
  const [deleteFromAllYears, setDeleteFromAllYears] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [helperToDelete, setHelperToDelete] = useState<Helper | null>(null);
  const [showHeaderMenu, setShowHeaderMenu] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showExportMenu, setShowExportMenu] = useState(false);
  const exportMenuRef = useRef<HTMLDivElement>(null);
  const [importing, setImporting] = useState(false);
  const [showImportConfirm, setShowImportConfirm] = useState(false);
  const [pendingImportFile, setPendingImportFile] = useState<File | null>(null);
  const importFileInputRef = useRef<HTMLInputElement>(null);
  const importFileInputRef2 = useRef<HTMLInputElement>(null);
  const [editingHelper, setEditingHelper] = useState<Helper | null>(null);
  const [editFormData, setEditFormData] = useState({ 
    first_name: '', 
    surname: '', 
    email: '', 
    phone: '',
    wunscheinsatzort: '',
    einsatzzeit: '',
    bereit_fuer_mehrere_einsaetze: 'Nein',
    anmerkung: '',
    aufbauhilfe: '',
    kuchenspende: 0,
    tshirt: ''
  });
  const [selectedFachbereiche, setSelectedFachbereiche] = useState<string[]>([]);
  const [saving, setSaving] = useState(false);
  const [showAssignmentWarning, setShowAssignmentWarning] = useState(false);
  const [pendingSave, setPendingSave] = useState<(() => void) | null>(null);
  const [affectedAssignments, setAffectedAssignments] = useState<{fachbereich: string, assignments: Helferpunkt[], rückmeldung: string | null}[]>([]);
  const [showIdentityChangeModal, setShowIdentityChangeModal] = useState(false);

  const helperMatchesPasstNichtMode = (helper: Helper): boolean => {
    if (!helper.feedback?.length) return false;
    if (showConfirmedPasstNicht) {
      return helper.feedback.some(fb => fb.rückmeldung === 'Passt nicht');
    }
    return helper.feedback.some(
      fb => fb.rückmeldung === 'Passt nicht' && !fb.passtNichtZentralBestaetigtAt
    );
  };

  useEffect(() => {
    if (!showPasstNichtOnly) {
      setShowConfirmedPasstNicht(false);
    }
  }, [showPasstNichtOnly]);

  useEffect(() => {
    fetchHelpers();
    fetchHelperAssignments();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    fetchMaintenanceStatus();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [year, token]);

  const fetchMaintenanceStatus = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/admin/years/maintenance`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.data.success && response.data.years && year) {
        const currentYearData = response.data.years.find((y: { year: number; disabled: boolean }) => y.year === year);
        if (currentYearData) {
          setYearMaintenanceDisabledLocal(currentYearData.disabled || false);
        }
      }
    } catch (err) {
      console.error('Error fetching maintenance status:', err);
      setYearMaintenanceDisabledLocal(false);
    }
  };

  // Set initial Fachbereich filter when component mounts or initialFachbereich changes
  useEffect(() => {
    if (initialFachbereich) {
      setFilterFachbereich(initialFachbereich);
      // Refetch helpers when initialFachbereich changes to ensure counts are correct
      fetchHelpers();
    }
  }, [initialFachbereich]);

  // Open edit dialog if initialHelperToEdit is provided
  useEffect(() => {
    if (initialHelperToEdit && helpers.length > 0) {
      const helperToEdit = helpers.find(h => h.id === initialHelperToEdit.id);
      if (helperToEdit) {
        handleOpenEdit(helperToEdit);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHelperToEdit, helpers]);

  const fetchHelpers = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_BASE_URL}/api/users`);
      if (response.data.success) {
        // Convert bereit_fuer_mehrere_einsaetze from integer (0/1) to string ('Nein'/'Ja')
        const convertedHelpers = response.data.users.map((helper: Helper) => 
          convertHelperBereitFuerMehrereEinsaetze(helper)
        );
        setHelpers(convertedHelpers);
      } else {
        setError('Fehler beim Laden der Helfer');
      }
    } catch (err: any) {
      setError('Verbindung zum Server nicht möglich');
    } finally {
      setLoading(false);
    }
  };

  const handlePasstNichtZentralBestaetigung = async (helperId: number, fachbereich: string, confirmed: boolean) => {
    const key = `${helperId}|${fachbereich}`;
    setPasstNichtBestaetigungSaving(key);
    setError('');
    try {
      await axios.patch(
        `${API_BASE_URL}/api/admin/helper/${helperId}/fachbereich/${encodeURIComponent(fachbereich)}/passt-nicht-zentral-bestaetigung`,
        { confirmed },
        { params: { year }, headers: { Authorization: `Bearer ${token}` } }
      );
      await fetchHelpers();
      onRefreshStatus?.();
    } catch (err: unknown) {
      console.error(err);
      setError(
        confirmed
          ? 'Zentrale Bestätigung konnte nicht gespeichert werden.'
          : 'Bestätigung konnte nicht entfernt werden.'
      );
    } finally {
      setPasstNichtBestaetigungSaving(null);
    }
  };

  const fetchHelperAssignments = async () => {
    try {
      const response = await axios.get(`${API_BASE_URL}/api/helpers/assignments`);
      if (response.data.success) {
        setHelperAssignments(response.data.assignments || {});
      }
    } catch (err) {
      console.error('Error fetching helper assignments:', err);
    }
  };

  // Find duplicates based on first_name + surname + email
  const findDuplicates = (helpersList: Helper[]): Helper[] => {
    const identifierMap = new Map<string, Helper[]>();
    
    helpersList.forEach(helper => {
      const identifier = helper.email 
        ? `${helper.first_name}|${helper.surname}|${helper.email}`
        : `${helper.first_name}|${helper.surname}|NULL`;
      
      if (!identifierMap.has(identifier)) {
        identifierMap.set(identifier, []);
      }
      identifierMap.get(identifier)!.push(helper);
    });
    
    // Return only helpers that have duplicates (appear more than once)
    const duplicates: Helper[] = [];
    identifierMap.forEach((helperGroup) => {
      if (helperGroup.length > 1) {
        duplicates.push(...helperGroup);
      }
    });
    
    return duplicates;
  };

  // Helfer: helps on race day OR offered Aufbauhilfe
  const isHelfer = (helper: Helper): boolean => {
    const helpsOnRaceDay = helper.hilft_nicht_am_wettkampftag !== 1;
    const hasAufbauhilfe = !!(helper.aufbauhilfe && helper.aufbauhilfe.trim() !== '');
    return helpsOnRaceDay || hasAufbauhilfe;
  };

  // Kuchenspender: sponsors cake (kuchenspende > 0)
  const isKuchenspender = (helper: Helper): boolean => {
    return (helper.kuchenspende !== null && helper.kuchenspende !== undefined && helper.kuchenspende > 0);
  };

  // Exempt from Fachbereich assignment: doesn't help on race day AND no aufbauhilfe
  // These helpers don't need Fachbereich - they are neither counted in "ohne Zuordnung" nor shown as such
  const isExemptFromFachbereichAssignment = (helper: Helper): boolean => {
    const doesntHelpOnRaceDay = helper.hilft_nicht_am_wettkampftag === 1;
    const hasNoAufbauhilfe = !(helper.aufbauhilfe && String(helper.aufbauhilfe).trim() !== '');
    return doesntHelpOnRaceDay && hasNoAufbauhilfe;
  };

  // Reset filters when their count becomes 0 (e.g. after editing - no helpers left without Zuordnung)
  useEffect(() => {
    if (helpers.length === 0) return;
    const modeSubset = showKuchenspender
      ? helpers.filter(h => (h.kuchenspende ?? 0) > 0)
      : helpers.filter(h => h.hilft_nicht_am_wettkampftag !== 1 || !!(h.aufbauhilfe && String(h.aufbauhilfe).trim() !== ''));
    const unassigned = modeSubset.filter(h => {
      if (isExemptFromFachbereichAssignment(h)) return false;
      return !hasEffectiveFachbereichZuordnung(h);
    }).length;
    const passtNicht = modeSubset.filter(h => helperMatchesPasstNichtMode(h)).length;
    if (showUnassignedOnly && unassigned === 0) setShowUnassignedOnly(false);
    if (showPasstNichtOnly && passtNicht === 0) setShowPasstNichtOnly(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [helpers, showKuchenspender, showConfirmedPasstNicht, showPasstNichtOnly]);

  // Step 1: Apply base filters (Fachbereich and search) to get base filtered list
  // This is used for calculating counts in buttons and dropdowns
  let baseFilteredHelpers = filterFachbereich
    ? helpers.filter(helper => 
        helper.fachbereiche && helper.fachbereiche.includes(filterFachbereich)
      )
    : helpers;

  // With Fachbereich filter: hide zentral bestätigtes „Passt nicht“ for that FB (list + Helfer count)
  if (filterFachbereich) {
    baseFilteredHelpers = baseFilteredHelpers.filter(
      helper => !isZentralBestaetigtesPasstNichtForFachbereich(helper, filterFachbereich)
    );
  }
  
  // Apply search filter if search query is provided
  if (searchQuery.trim()) {
    baseFilteredHelpers = baseFilteredHelpers.filter(helper =>
      helperMatchesSearch(searchQuery, helper.first_name, helper.surname, helper.email)
    );
  }

  // Step 2: Apply Helfer/Kuchenspender mode: OFF = show Helfer, ON = show Kuchenspender only
  let workingFilteredHelpers = baseFilteredHelpers;
  if (!showKuchenspender) {
    // Helfer mode: show helpers who help on race day OR offered Aufbauhilfe
    workingFilteredHelpers = workingFilteredHelpers.filter(helper => isHelfer(helper));
  } else {
    // Kuchenspender mode: show only people who sponsor cake
    workingFilteredHelpers = workingFilteredHelpers.filter(helper => isKuchenspender(helper));
  }
  
  if (showDuplicatesOnly) {
    workingFilteredHelpers = findDuplicates(workingFilteredHelpers);
  }
  
  if (showUnassignedOnly) {
    workingFilteredHelpers = workingFilteredHelpers.filter(helper => {
      if (isExemptFromFachbereichAssignment(helper)) return false;
      return !hasEffectiveFachbereichZuordnung(helper);
    });
  }

  if (showPasstNichtOnly) {
    workingFilteredHelpers = workingFilteredHelpers.filter(helperMatchesPasstNichtMode);
  }

  // Count helpers ready for multiple assignments (for "Helfer bereit für mehr Einsätze" filter) - before applying that filter
  const readyForMultipleCount = workingFilteredHelpers.filter(helper =>
    convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) === 'Ja'
  ).length;
  const totalBeforeReadyFilter = workingFilteredHelpers.length;

  if (showOnlyReadyForMultiple) {
    workingFilteredHelpers = workingFilteredHelpers.filter(helper =>
      convertBereitFuerMehrereEinsaetze(helper.bereit_fuer_mehrere_einsaetze) === 'Ja'
    );
  }

  // Step 3: Calculate counts for filter options (duplicates, unassigned, passt nicht)
  const duplicatesCount = findDuplicates(workingFilteredHelpers).length;
  const unassignedCount = workingFilteredHelpers.filter(helper => {
    if (isExemptFromFachbereichAssignment(helper)) return false;
    return !hasEffectiveFachbereichZuordnung(helper);
  }).length;
  const passtNichtCount = workingFilteredHelpers.filter(helper => helperMatchesPasstNichtMode(helper)).length;

  // Counts for Helfer/Kuchenspender toggle (from base filtered list)
  const helferCount = baseFilteredHelpers.filter(helper => isHelfer(helper)).length;
  const kuchenspenderHelpers = baseFilteredHelpers.filter(helper => isKuchenspender(helper));
  const kuchenspenderCount = kuchenspenderHelpers.length;
  const kuchenCount = kuchenspenderHelpers.reduce((sum, h) => sum + (h.kuchenspende ?? 0), 0);

  // Step 4: Calculate helper count per Fachbereich (respects current mode: Helfer or Kuchenspender)
  let helpersForCounts = helpers;
  if (searchQuery.trim()) {
    helpersForCounts = helpers.filter(helper =>
      helperMatchesSearch(searchQuery, helper.first_name, helper.surname, helper.email)
    );
  }
  let helpersForFachbereichCounts = helpersForCounts.filter(helper =>
    !showKuchenspender ? isHelfer(helper) : isKuchenspender(helper)
  );
  // When Passt nicht filter is active, counts should reflect Passt nicht per Fachbereich
  if (showPasstNichtOnly) {
    helpersForFachbereichCounts = helpersForFachbereichCounts.filter(helper => helperMatchesPasstNichtMode(helper));
  }
  
  const getFachbereichHelperCount = (fachbereich: string): number => {
    return helpersForFachbereichCounts.filter(helper => {
      if (!helper.fachbereiche?.includes(fachbereich)) return false;
      return !isZentralBestaetigtesPasstNichtForFachbereich(helper, fachbereich);
    }).length;
  };

  // Step 5: Final filtered helpers for display (sorted by surname, first name, email)
  const filteredHelpers = [...workingFilteredHelpers].sort((a, b) => {
    const surnameA = (a.surname || '').toLowerCase();
    const surnameB = (b.surname || '').toLowerCase();
    if (surnameA !== surnameB) {
      return surnameA.localeCompare(surnameB, 'de');
    }
    const firstNameA = (a.first_name || '').toLowerCase();
    const firstNameB = (b.first_name || '').toLowerCase();
    if (firstNameA !== firstNameB) {
      return firstNameA.localeCompare(firstNameB, 'de');
    }
    const emailA = (a.email || '').toLowerCase();
    const emailB = (b.email || '').toLowerCase();
    return emailA.localeCompare(emailB, 'de');
  });

  // Calculate hours between two time strings (HH:MM format)
  const calculateHours = (timeFrom: string, timeTo: string | null): number => {
    if (!timeFrom || !timeTo) {
      return 0;
    }
    
    try {
      const [hoursFrom, minutesFrom] = timeFrom.split(':').map(Number);
      const [hoursTo, minutesTo] = timeTo.split(':').map(Number);
      
      const fromMinutes = hoursFrom * 60 + minutesFrom;
      const toMinutes = hoursTo * 60 + minutesTo;
      
      // Handle case where timeTo is on the next day
      let diffMinutes = toMinutes - fromMinutes;
      if (diffMinutes < 0) {
        diffMinutes += 24 * 60; // Add 24 hours
      }
      
      return diffMinutes / 60;
    } catch (error) {
      return 0;
    }
  };

  const exportToExcel = () => {
    if (filteredHelpers.length === 0) return;

    // Prepare data for Excel (use filtered helpers)
    const excelData = filteredHelpers.map((helper, index) => {
      const date = new Date(helper.created_at);
      const assignments = sortAssignmentsByDateAndTime(helperAssignments[helper.id] || []);
      const einsatzorte = assignments.length > 0 
        ? assignments.map(hp => {
            const weekday = getWeekdayAbbreviation(hp.einsatzdatum);
            const timeStr = `${hp.einsatzuhrzeit}${hp.einsatzuhrzeit_bis ? ` - ${hp.einsatzuhrzeit_bis}` : ''}`;
            const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
            return `${hp.einsatzort} (${timeDisplay}) [${hp.fachbereich}]`;
          }).join('; ')
        : '';
      
      // Calculate total Einsatzstunden for this helper
      let totalEinsatzstunden = 0;
      assignments.forEach((hp) => {
        if (hp.einsatzuhrzeit && hp.einsatzuhrzeit_bis) {
          totalEinsatzstunden += calculateHours(hp.einsatzuhrzeit, hp.einsatzuhrzeit_bis);
        }
      });
      const einsatzstundenText = totalEinsatzstunden > 0 
        ? totalEinsatzstunden.toFixed(2).replace('.', ',')
        : '';
      
      // Format feedback for Excel - only include if there's actual feedback
      let feedbackText = '';
      if (helper.feedback && helper.feedback.length > 0) {
        const feedbackEntries = helper.feedback.map(fb => {
          let fbText = '';
          if (fb.rückmeldung || fb.bemerkung) {
            fbText = fb.fachbereich + ':';
            // Handle "Passt nicht" - show bemerkung if available
            if (fb.rückmeldung) {
              const isPasstNicht = fb.rückmeldung === 'Passt nicht';
              const hasBemerkung = fb.bemerkung && fb.bemerkung.trim() !== '';
              if (isPasstNicht && !hasBemerkung) {
                fbText += ' Rückmeldung: Passt nicht';
              } else {
                fbText += ` Rückmeldung: ${fb.rückmeldung}`;
              }
            }
            if (fb.bemerkung) fbText += ` Bemerkung: ${fb.bemerkung}`;
          }
          return fbText;
        }).filter(text => text.length > 0); // Only include entries with actual feedback
        
        if (feedbackEntries.length > 0) {
          feedbackText = feedbackEntries.join('; ');
        }
      }
      
      return {
        'Nr.': index + 1,
        'Registriert am': date.toLocaleDateString('de-DE', {
          day: '2-digit',
          month: '2-digit',
          year: 'numeric'
        }),
        'Registriert um': date.toLocaleTimeString('de-DE', {
          hour: '2-digit',
          minute: '2-digit'
        }),
        'Vorname': helper.first_name,
        'Nachname': helper.surname,
        'E-Mail': helper.email || '',
        'Telefon': helper.phone || '',
        'Wunscheinsatzort': helper.wunscheinsatzort || '',
        'Einsatzzeit': helper.einsatzzeit || '',
        'Bereit für mehrere Einsätze': helper.bereit_fuer_mehrere_einsaetze || '',
        'Anmerkung': helper.anmerkung || '',
        'Aufbauhilfe': helper.aufbauhilfe || '',
        'T-Shirt': helper.tshirt || '',
        'Kuchenspende': helper.kuchenspende !== null && helper.kuchenspende !== undefined ? helper.kuchenspende : '',
        'Zuordnung zu Fachbereich': helper.fachbereiche && helper.fachbereiche.length > 0 ? helper.fachbereiche.join(', ') : '',
        'Rückmeldung & Bemerkung': feedbackText,
        'Einsatzorte': einsatzorte,
        'Einsatzstunden (Stunden)': einsatzstundenText
      };
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Format phone number column as text to preserve leading zeros
    formatPhoneColumnAsText(worksheet, 'Telefon');
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Helfer');

    // Set column widths
    const columnWidths = [
      { wch: 5 },   // Nr.
      { wch: 12 },  // Registriert am
      { wch: 12 },  // Registriert um
      { wch: 15 },  // Vorname
      { wch: 15 },  // Nachname
      { wch: 25 },  // E-Mail
      { wch: 15 },  // Telefon
      { wch: 20 },  // Wunscheinsatzort
      { wch: 15 },  // Einsatzzeit
      { wch: 25 },  // Bereit für mehrere Einsätze
      { wch: 30 },  // Anmerkung
      { wch: 10 },  // T-Shirt
      { wch: 12 },  // Kuchenspende
      { wch: 20 },  // Zuordnung zu Fachbereich
      { wch: 40 },  // Rückmeldung & Bemerkung
      { wch: 40 },  // Einsatzorte
      { wch: 15 }   // Einsatzstunden
    ];
    worksheet['!cols'] = columnWidths;

    // Generate filename with current date
    const today = new Date();
    const dateStr = today.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    const safeFachbereich = filterFachbereich ? filterFachbereich.replace(/[^a-zA-Z0-9]/g, '_') : 'Alle';
    const filename = `Roemerman_${year || 2026}_Helfer_${safeFachbereich}_${dateStr}.xlsx`;

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  const exportToRegistrationFormat = () => {
    // Filter helpers to only those assigned to Fachbereiche the Fachbereich Admin has access to
    let helpersToExport = filteredHelpers;
    
    if (!isAdmin() && user?.role === 'Fachbereich Administrator' && user?.fachbereiche && user.fachbereiche.length > 0) {
      const userFachbereiche = user.fachbereiche;
      helpersToExport = filteredHelpers.filter(helper => {
        // Only include helpers that have at least one Fachbereich assigned to the user
        return helper.fachbereiche && helper.fachbereiche.some(fb => userFachbereiche.includes(fb));
      }).map(helper => {
        // Filter Fachbereiche to only those the user has access to
        const filteredFachbereiche = helper.fachbereiche.filter(fb => userFachbereiche.includes(fb));
        return {
          ...helper,
          fachbereiche: filteredFachbereiche
        };
      });
    }

    if (helpersToExport.length === 0) {
      alert('Keine Helfer zum Exportieren gefunden.');
      return;
    }

    // Prepare data in registration import format
    const excelData = helpersToExport.map((helper) => {
      return {
        'Vorname': helper.first_name,
        'Nachname': helper.surname,
        'E-Mail': helper.email || '',
        'Telefon': helper.phone || '',
        'Fachbereiche': helper.fachbereiche && helper.fachbereiche.length > 0 ? helper.fachbereiche.join(', ') : '',
        'Wunscheinsatzort': helper.wunscheinsatzort || '',
        'Einsatzzeit': helper.einsatzzeit || '',
        'Bereit für mehrere Einsätze': helper.bereit_fuer_mehrere_einsaetze || '',
        'Anmerkung': helper.anmerkung || '',
        'Kuchenspende': helper.kuchenspende !== null && helper.kuchenspende !== undefined ? helper.kuchenspende : '',
        'T-Shirt': helper.tshirt || ''
      };
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    
    // Format phone number column as text to preserve leading zeros
    formatPhoneColumnAsText(worksheet, 'Telefon');
    
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Helfer');

    // Set column widths
    const columnWidths = [
      { wch: 15 },  // Vorname
      { wch: 15 },  // Nachname
      { wch: 25 },  // E-Mail
      { wch: 15 },  // Telefon
      { wch: 20 },  // Fachbereiche
      { wch: 20 },  // Wunscheinsatzort
      { wch: 15 },  // Einsatzzeit
      { wch: 25 },  // Bereit für mehrere Einsätze
      { wch: 30 },  // Anmerkung
      { wch: 12 },  // Kuchenspende
      { wch: 10 }   // T-Shirt
    ];
    worksheet['!cols'] = columnWidths;

    // Generate filename with current date
    const today = new Date();
    const dateStr = today.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    
    let filename = '';
    if (!isAdmin() && user?.role === 'Fachbereich Administrator' && user?.fachbereiche && user.fachbereiche.length > 0) {
      const safeFachbereiche = user.fachbereiche.map(fb => fb.replace(/[^a-zA-Z0-9]/g, '_')).join('_');
      filename = `Roemerman_${year || 2026}_Helfer_Import_${safeFachbereiche}_${dateStr}.xlsx`;
    } else {
      const safeFachbereich = filterFachbereich ? filterFachbereich.replace(/[^a-zA-Z0-9]/g, '_') : 'Alle';
      filename = `Roemerman_${year || 2026}_Helfer_Import_${safeFachbereich}_${dateStr}.xlsx`;
    }

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  const exportZuordnungen = () => {
    // Filter helpers to only those assigned to Fachbereiche the Fachbereich Admin has access to
    let helpersToExport = filteredHelpers;
    
    if (!isAdmin() && user?.role === 'Fachbereich Administrator' && user?.fachbereiche && user.fachbereiche.length > 0) {
      const userFachbereiche = user.fachbereiche;
      helpersToExport = filteredHelpers.filter(helper => {
        // Only include helpers that have at least one Fachbereich assigned to the user
        return helper.fachbereiche && helper.fachbereiche.some(fb => userFachbereiche.includes(fb));
      }).map(helper => {
        // Filter Fachbereiche to only those the user has access to
        const filteredFachbereiche = helper.fachbereiche.filter(fb => userFachbereiche.includes(fb));
        return {
          ...helper,
          fachbereiche: filteredFachbereiche
        };
      });
    }

    if (helpersToExport.length === 0) {
      alert('Keine Helfer zum Exportieren gefunden.');
      return;
    }

    // Prepare data in Zuordnung import format
    const excelData = helpersToExport.map((helper) => {
      // Get Fachbereiche (comma-separated)
      const fachbereicheStr = helper.fachbereiche && helper.fachbereiche.length > 0 
        ? helper.fachbereiche.join(', ') 
        : '';

      // Format Rückmeldung & Bemerkung
      // Format: "Fachbereich: Rückmeldung: X Bemerkung: Y; Fachbereich2: Rückmeldung: X2 Bemerkung: Y2"
      let feedbackStr = '';
      if (helper.feedback && Array.isArray(helper.feedback) && helper.feedback.length > 0) {
        const feedbackEntries = helper.feedback
          .filter(fb => fb.fachbereich && (fb.rückmeldung || fb.bemerkung))
          .map(fb => {
            let entry = `${fb.fachbereich}:`;
            if (fb.rückmeldung && fb.rückmeldung.trim()) {
              entry += ` Rückmeldung: ${fb.rückmeldung.trim()}`;
            }
            if (fb.bemerkung && fb.bemerkung.trim()) {
              entry += ` Bemerkung: ${fb.bemerkung.trim()}`;
            }
            return entry;
          });
        feedbackStr = feedbackEntries.join('; ');
      }

      return {
        'Vorname': helper.first_name,
        'Nachname': helper.surname,
        'E-Mail': helper.email || '',
        'Zuordnung zu Fachbereich': fachbereicheStr,
        'Rückmeldung & Bemerkung': feedbackStr || ''
      };
    });

    // Create workbook and worksheet
    const worksheet = XLSX.utils.json_to_sheet(excelData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'Zuordnungen');

    // Set column widths
    const columnWidths = [
      { wch: 15 },  // Vorname
      { wch: 15 },  // Nachname
      { wch: 25 },  // E-Mail
      { wch: 30 },  // Zuordnung zu Fachbereich
      { wch: 50 }   // Rückmeldung & Bemerkung
    ];
    worksheet['!cols'] = columnWidths;

    // Generate filename with current date
    const today = new Date();
    const dateStr = today.toLocaleDateString('de-DE', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit'
    }).replace(/\//g, '-');
    
    let filename = '';
    if (!isAdmin() && user?.role === 'Fachbereich Administrator' && user?.fachbereiche && user.fachbereiche.length > 0) {
      const safeFachbereiche = user.fachbereiche.map(fb => fb.replace(/[^a-zA-Z0-9]/g, '_')).join('_');
      filename = `Roemerman_${year || 2026}_Zuordnungen_${safeFachbereiche}_${dateStr}.xlsx`;
    } else {
      const safeFachbereich = filterFachbereich ? filterFachbereich.replace(/[^a-zA-Z0-9]/g, '_') : 'Alle';
      filename = `Roemerman_${year || 2026}_Zuordnungen_${safeFachbereich}_${dateStr}.xlsx`;
    }

    // Download file
    XLSX.writeFile(workbook, filename);
  };

  const handleImportAssignments = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const input = event.target;
    const files = input.files;
    
    // Prevent import if year is read-only
    if (yearMaintenanceDisabled) {
      alert(`Das Jahr ${year} ist schreibgeschützt. Import ist nicht möglich.`);
      if (input) {
        input.value = '';
      }
      return;
    }
    
    try {
      if (!files || files.length === 0) {
        return;
      }
      
      const file = files[0];

      if (!file.name.match(/\.(xlsx|xls)$/i)) {
        alert('Bitte wählen Sie eine Excel-Datei (.xlsx oder .xls)');
        input.value = '';
        return;
      }
      
      // Show confirmation dialog with file details
      setPendingImportFile(file);
      setShowImportConfirm(true);
      
    } catch (error) {
      console.error('=== ERROR in handleImportAssignments ===', error);
      alert('Fehler beim Verarbeiten der Datei: ' + (error instanceof Error ? error.message : String(error)));
      if (input) {
        input.value = '';
      }
    }
  };

  const handleConfirmImport = async () => {
    if (!pendingImportFile) {
      console.error('No pending file to import');
      return;
    }

    setShowImportConfirm(false);
    setImporting(true);
    pauseHealthCheck(); // Pause health checks during import to prevent false "backend unavailable" warnings
    setError('');

    const file = pendingImportFile;
    setPendingImportFile(null);
    
    // Reset file inputs
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
    if (importFileInputRef2.current) {
      importFileInputRef2.current.value = '';
    }

    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await axios.post(`${API_BASE_URL}/api/helpers/fachbereiche/import`, formData, {
        headers: {
          'Authorization': `Bearer ${token}`
          // Don't set Content-Type manually - let axios set it with the correct boundary
        }
      });

      if (response.data.success) {
        let message = response.data.message;
        
        if (response.data.errors && response.data.errors.length > 0) {
          message += '\n\nFehler:\n' + response.data.errors.slice(0, 10).join('\n');
          if (response.data.errors.length > 10) {
            message += `\n... und ${response.data.errors.length - 10} weitere Fehler`;
          }
        }
        
        alert(message);
        
        // Refresh data
        await fetchHelpers();
        await fetchHelperAssignments();
      } else {
        let errorMessage = response.data.message || 'Fehler beim Importieren';
        if (response.data.errors && response.data.errors.length > 0) {
          errorMessage += '\n\nFehler:\n' + response.data.errors.slice(0, 10).join('\n');
          if (response.data.errors.length > 10) {
            errorMessage += `\n... und ${response.data.errors.length - 10} weitere Fehler`;
          }
        }
        alert(errorMessage);
      }
    } catch (err: any) {
      let errorMessage = 'Fehler beim Importieren der Zuordnungen';
      if (err.response?.data?.message) {
        errorMessage = err.response.data.message;
        if (err.response.data.errors && err.response.data.errors.length > 0) {
          errorMessage += '\n\nFehler:\n' + err.response.data.errors.slice(0, 10).join('\n');
          if (err.response.data.errors.length > 10) {
            errorMessage += `\n... und ${err.response.data.errors.length - 10} weitere Fehler`;
          }
        }
      } else if (err.message) {
        errorMessage += `: ${err.message}`;
      }
      alert(errorMessage);
    } finally {
      setImporting(false);
      resumeHealthCheck(); // Resume health checks after import completes
    }
  };

  const handleCancelImport = () => {
    setShowImportConfirm(false);
    setPendingImportFile(null);
    // Reset file inputs after cancellation
    if (importFileInputRef.current) {
      importFileInputRef.current.value = '';
    }
    if (importFileInputRef2.current) {
      importFileInputRef2.current.value = '';
    }
  };

  const handleImportClick = () => {
    // Try first ref (export menu), fallback to second ref (header menu)
    const fileInput = importFileInputRef.current || importFileInputRef2.current;
    if (fileInput) {
      // Use setTimeout to ensure the click happens after any state updates
      setTimeout(() => {
        fileInput.click();
      }, 0);
    } else {
      console.error('No file input refs found!');
      alert('Fehler: Datei-Eingabefeld nicht gefunden. Bitte Seite neu laden.');
    }
  };

  const handleOpenEdit = async (helper: Helper) => {
    setEditingHelper(helper);
    
    // Initialize selected Fachbereiche from helper's current assignments
    // For Fachbereich Administrators, filter to only their assigned Fachbereiche
    let initialFachbereiche = helper.fachbereiche || [];
    if (!isAdmin() && user?.role === 'Fachbereich Administrator' && user?.fachbereiche && user.fachbereiche.length > 0) {
      initialFachbereiche = initialFachbereiche.filter(fb => user.fachbereiche!.includes(fb));
    }
    
    const initialFormData = {
      first_name: helper.first_name,
      surname: helper.surname,
      email: helper.email || '',
      phone: helper.phone || '',
      wunscheinsatzort: helper.wunscheinsatzort || '',
      einsatzzeit: helper.einsatzzeit || '',
      // Convert backend boolean/string to frontend 'Ja'/'Nein' string
      bereit_fuer_mehrere_einsaetze: (() => {
        const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
        if (bereitValue === undefined || bereitValue === null) {
          return 'Nein';
        }
        // Type-safe conversion: handle boolean, string, or number
        if (bereitValue === true || bereitValue === 'Ja' || bereitValue === '1') {
          return 'Ja';
        }
        // Handle number case separately to avoid TypeScript error
        if (typeof bereitValue === 'number' && bereitValue === 1) {
          return 'Ja';
        }
        return 'Nein';
      })(),
      anmerkung: helper.anmerkung || '',
      aufbauhilfe: helper.aufbauhilfe || '',
      kuchenspende: helper.kuchenspende !== null && helper.kuchenspende !== undefined ? helper.kuchenspende : 0,
      tshirt: helper.tshirt || ''
    };
    
    setEditFormData(initialFormData);
    setSelectedFachbereiche(initialFachbereiche);
    
    // Store original values for change detection
    setOriginalEditFormData({
      ...initialFormData,
      fachbereiche: initialFachbereiche
    });
    setShowUnsavedEditConfirm(false);
  };

  // Check if there are unsaved changes - now using registrationFormSelectedFachbereiche when using RegistrationForm
  const hasUnsavedEditChanges = useMemo(() => {
    if (!originalEditFormData || !editingHelper) return false;
    
    // Compare arrays properly - use registrationFormSelectedFachbereiche if available
    const currentFachbereiche = registrationFormSelectedFachbereiche.length > 0 ? registrationFormSelectedFachbereiche : selectedFachbereiche;
    const fachbereicheChanged = JSON.stringify([...currentFachbereiche].sort()) !== JSON.stringify([...originalEditFormData.fachbereiche].sort());
    
    return (
      editFormData.first_name !== originalEditFormData.first_name ||
      editFormData.surname !== originalEditFormData.surname ||
      editFormData.email !== originalEditFormData.email ||
      editFormData.phone !== originalEditFormData.phone ||
      editFormData.wunscheinsatzort !== originalEditFormData.wunscheinsatzort ||
      editFormData.einsatzzeit !== originalEditFormData.einsatzzeit ||
      editFormData.bereit_fuer_mehrere_einsaetze !== originalEditFormData.bereit_fuer_mehrere_einsaetze ||
      editFormData.anmerkung !== originalEditFormData.anmerkung ||
      editFormData.aufbauhilfe !== originalEditFormData.aufbauhilfe ||
      editFormData.kuchenspende !== originalEditFormData.kuchenspende ||
      editFormData.tshirt !== originalEditFormData.tshirt ||
      fachbereicheChanged
    );
  }, [editFormData, selectedFachbereiche, registrationFormSelectedFachbereiche, originalEditFormData, editingHelper]);

  const handleCloseEdit = useCallback(() => {
    setEditingHelper(null);
    setEditFormData({ 
      first_name: '', 
      surname: '', 
      email: '', 
      phone: '',
      wunscheinsatzort: '',
      einsatzzeit: '',
      bereit_fuer_mehrere_einsaetze: 'Nein',
      anmerkung: '',
      aufbauhilfe: '',
      kuchenspende: 0,
      tshirt: ''
    });
    setSelectedFachbereiche([]);
    setOriginalEditFormData(null);
    setError('');
    setShowUnsavedEditConfirm(false);
  }, []);

  // Handle Escape key to close dialog or cancel confirmations
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        // If unsaved edit confirmation is open, close it (equivalent to "Fortfahren")
        if (showUnsavedEditConfirm) {
          setShowUnsavedEditConfirm(false);
          return;
        }
        // If cleanup confirmation is open, cancel it
        if (showCleanupConfirm && !cleaning) {
          setShowCleanupConfirm(false);
          return;
        }
        // If delete confirmation is open, cancel it
        if (deleteConfirmId !== null && !deleting) {
          setDeleteConfirmId(null);
          setHelperToDelete(null);
          return;
        }
        // If editing helper with unsaved changes, show confirmation
        if (editingHelper && hasUnsavedEditChanges) {
          e.preventDefault();
          e.stopPropagation();
          setShowUnsavedEditConfirm(true);
          return;
        }
        // If editing helper without changes, close edit form
        if (editingHelper) {
          handleCloseEdit();
          return;
        }
        // Otherwise, close the main dialog
        onClose();
      }
    };
    window.addEventListener('keydown', handleEscape);
    return () => window.removeEventListener('keydown', handleEscape);
  }, [onClose, editingHelper, showCleanupConfirm, deleteConfirmId, cleaning, deleting, showUnsavedEditConfirm, hasUnsavedEditChanges, handleCloseEdit]);

  const registrationFormRef = useRef<HTMLFormElement>(null);

  const handleConfirmSaveAndClose = async () => {
    setShowUnsavedEditConfirm(false);
    // Trigger form submit by submitting the RegistrationForm
    if (registrationFormRef.current) {
      registrationFormRef.current.requestSubmit();
    }
  };

  const handleConfirmDiscard = () => {
    setShowUnsavedEditConfirm(false);
    handleCloseEdit();
  };

  const handleConfirmContinue = () => {
    setShowUnsavedEditConfirm(false);
    // Just close the confirmation dialog, keep editing
  };

  // Handler for checking assignment warnings before form submission (used with RegistrationForm)
  const handleBeforeSubmit = async (formData: {
    firstName: string;
    surname: string;
    email: string;
    phone: string;
    selectedFachbereiche: string[];
    wunscheinsatzort: string;
    einsatzzeit: string;
    bereitFuerMehrereEinsaetze: string;
    anmerkung: string;
    aufbauhilfe: string;
    kuchenspende: number;
    tshirt: string;
  }): Promise<boolean> => {
    if (!editingHelper) return false;

    // Check if year maintenance is disabled
    if (yearMaintenanceDisabled && year) {
      const errorMsg = `Die Bearbeitung von Daten für Jahr ${year} ist deaktiviert. Bitte aktivieren Sie die Bearbeitung für dieses Jahr in der Jahresverwaltung.`;
      setError(errorMsg);
      console.error('Save blocked - year maintenance disabled:', { year, helperId: editingHelper.id });
      return false;
    }

    // Update form data state to match RegistrationForm values
    setEditFormData({
      first_name: formData.firstName,
      surname: formData.surname,
      email: formData.email,
      phone: formData.phone,
      wunscheinsatzort: formData.wunscheinsatzort,
      einsatzzeit: formData.einsatzzeit,
      bereit_fuer_mehrere_einsaetze: formData.bereitFuerMehrereEinsaetze,
      anmerkung: formData.anmerkung,
      aufbauhilfe: formData.aufbauhilfe,
      kuchenspende: formData.kuchenspende,
      tshirt: formData.tshirt
    });
    setSelectedFachbereiche(formData.selectedFachbereiche);
    setRegistrationFormSelectedFachbereiche(formData.selectedFachbereiche);

    // Check if any Fachbereiche are being removed
    if (originalEditFormData) {
      const removedFachbereiche = originalEditFormData.fachbereiche.filter(
        fb => !formData.selectedFachbereiche.includes(fb)
      );
      
      // Check if removed Fachbereiche have position assignments or Rückmeldung
      const assignments = helperAssignments[editingHelper.id] || [];
      const feedbackMap = new Map<string, string | null>();
      if (editingHelper.feedback && Array.isArray(editingHelper.feedback)) {
        editingHelper.feedback.forEach((fb: HelperFeedback) => {
          const r = (fb.rückmeldung || '').trim();
          if (r) feedbackMap.set(fb.fachbereich, fb.rückmeldung);
        });
      }
      const affected: {fachbereich: string, assignments: Helferpunkt[], rückmeldung: string | null}[] = [];
      
      removedFachbereiche.forEach(fb => {
        const fbAssignments = assignments.filter(a => a.fachbereich === fb);
        const rückmeldung = feedbackMap.get(fb) || null;
        if (fbAssignments.length > 0 || rückmeldung) {
          affected.push({ fachbereich: fb, assignments: fbAssignments, rückmeldung });
        }
      });
      
      if (affected.length > 0) {
        // Show warning modal and prevent submission
        setAffectedAssignments(affected);
        setShowAssignmentWarning(true);
        setPendingSave(() => async () => {
          // Update form data before saving
          setEditFormData({
            first_name: formData.firstName,
            surname: formData.surname,
            email: formData.email,
            phone: formData.phone,
            wunscheinsatzort: formData.wunscheinsatzort,
            einsatzzeit: formData.einsatzzeit,
            bereit_fuer_mehrere_einsaetze: formData.bereitFuerMehrereEinsaetze,
            anmerkung: formData.anmerkung,
            aufbauhilfe: formData.aufbauhilfe,
            kuchenspende: formData.kuchenspende,
            tshirt: formData.tshirt
          });
          setSelectedFachbereiche(formData.selectedFachbereiche);
          setRegistrationFormSelectedFachbereiche(formData.selectedFachbereiche);
          // Pass fachbereiche directly - setState is async, performSave would read stale state otherwise
          await performSave(formData.selectedFachbereiche);
        });
        return false; // Prevent submission
      }
    }
    
    // For Fachbereich Administrators, ensure at least one Fachbereich is selected
    if (user?.role === 'Fachbereich Administrator' && formData.selectedFachbereiche.length === 0) {
      setError('Bitte wählen Sie mindestens einen Fachbereich aus');
      return false;
    }
    
    // No assignments to remove, allow submission
    return true;
  };

  const performSave = async (fachbereicheOverride?: string[]) => {
    if (!editingHelper) return;

    setSaving(true);
    setError('');
    setShowAssignmentWarning(false);
    setPendingSave(null);

    try {
      // Use override when provided (e.g. from pendingSave after assignment warning - setState is async)
      // Otherwise use current form data (may have been updated by RegistrationForm)
      const currentFachbereiche = fachbereicheOverride !== undefined
        ? fachbereicheOverride
        : (registrationFormSelectedFachbereiche.length > 0 ? registrationFormSelectedFachbereiche : selectedFachbereiche);
      
      // Update basic helper info (including all new fields and Fachbereiche)
      const response = await axios.put(`${API_BASE_URL}/api/helper/${editingHelper.id}`, {
        first_name: editFormData.first_name,
        surname: editFormData.surname,
        email: editFormData.email,
        phone: editFormData.phone,
        wunscheinsatzort: editFormData.wunscheinsatzort,
        einsatzzeit: editFormData.einsatzzeit,
        bereit_fuer_mehrere_einsaetze: editFormData.bereit_fuer_mehrere_einsaetze,
        anmerkung: editFormData.anmerkung,
        aufbauhilfe: editFormData.aufbauhilfe,
        kuchenspende: editFormData.kuchenspende,
        tshirt: editFormData.tshirt,
        fachbereiche: currentFachbereiche
      }, {
        headers: {
          'Authorization': `Bearer ${token}`
          }
        });

      if (response.data.success) {
        // Refresh the helper list and assignments
        await fetchHelpers();
        await fetchHelperAssignments();
        setEditingHelper(null);
        setEditFormData({ 
          first_name: '', 
          surname: '', 
          email: '', 
          phone: '',
          wunscheinsatzort: '',
          einsatzzeit: '',
          bereit_fuer_mehrere_einsaetze: 'Nein',
          anmerkung: '',
          aufbauhilfe: '',
          kuchenspende: 0,
          tshirt: ''
        });
        setSelectedFachbereiche([]);
        setRegistrationFormSelectedFachbereiche([]);
        setOriginalEditFormData(null);
        setShowUnsavedEditConfirm(false);
        setAffectedAssignments([]);
      } else {
        setError(response.data.message || 'Fehler beim Aktualisieren des Helfers');
      }
    } catch (err: any) {
      let errorMessage = 'Fehler beim Aktualisieren des Helfers';
      let details = '';

      if (err.response) {
        // Handle 403 Forbidden (year maintenance disabled)
        if (err.response.status === 403) {
          errorMessage = err.response.data?.message || `Die Bearbeitung von Daten für Jahr ${year || 'das aktuelle Jahr'} ist deaktiviert. Bitte aktivieren Sie die Bearbeitung für dieses Jahr in der Jahresverwaltung.`;
          if (year) {
            setYearMaintenanceDisabledLocal(true);
          }
        } else if (err.response?.data) {
          errorMessage = err.response.data.message || errorMessage;
          if (err.response.data.details) {
            details = ` (Details: ${err.response.data.details})`;
          }
        }
      } else if (err.message) {
        details = ` (${err.message})`;
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        errorMessage = 'Verbindung zum Server nicht möglich';
        details = '. Bitte stellen Sie sicher, dass der Backend-Server läuft.';
      }

      setError(errorMessage + details);
    } finally {
      setSaving(false);
    }
  };

  const handleEditSuccess = async (firstName: string, surname: string) => {
    // Refresh the helper list and assignments after successful edit
    await fetchHelpers();
    await fetchHelperAssignments();
    onRefreshStatus?.();
    setEditingHelper(null);
    setEditFormData({ 
      first_name: '', 
      surname: '', 
      email: '', 
      phone: '',
      wunscheinsatzort: '',
      einsatzzeit: '',
          bereit_fuer_mehrere_einsaetze: 'Nein',
          anmerkung: '',
          aufbauhilfe: '',
          kuchenspende: 0,
          tshirt: ''
        });
    setSelectedFachbereiche([]);
    setRegistrationFormSelectedFachbereiche([]);
    setOriginalEditFormData(null);
    setShowUnsavedEditConfirm(false);
    setAffectedAssignments([]);
  };

  const handleDeleteHelper = async (helperId: number, deleteFromAllYears: boolean = false) => {
    setDeleting(true);
    setError('');
    setOpenMenuId(null);

    try {
      const url = deleteFromAllYears
        ? `${API_BASE_URL}/api/helper/${helperId}?allYears=true`
        : `${API_BASE_URL}/api/helper/${helperId}`;
      const response = await axios.delete(url);

      if (response.data.success) {
        // Refresh the helper list and assignments
        await fetchHelpers();
        await fetchHelperAssignments();
        onRefreshStatus?.();
        setDeleteConfirmId(null);
        setHelperToDelete(null);
        setDeleteFromAllYears(false);
        if (deleteFromAllYears && response.data.message) {
          alert(response.data.message);
        }
      } else {
        setError(response.data.message || 'Fehler beim Löschen des Helfers');
      }
    } catch (err: any) {
      let errorMessage = 'Fehler beim Löschen des Helfers';
      let details = '';

      if (err.response?.data) {
        errorMessage = err.response.data.message || errorMessage;
        if (err.response.data.details) {
          details = ` (Details: ${err.response.data.details})`;
        }
      } else if (err.message) {
        details = ` (${err.message})`;
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        errorMessage = 'Verbindung zum Server nicht möglich';
        details = '. Bitte stellen Sie sicher, dass der Backend-Server läuft.';
      }

      setError(`${errorMessage}${details}`);
    } finally {
      setDeleting(false);
    }
  };

  const handleCleanup = async () => {
    // Safety check: Only admins can delete all helpers
    if (!isAdmin()) {
      setError('Nur Administratoren können alle Helfer löschen');
      setShowCleanupConfirm(false);
      return;
    }

    // Prevent deletion if year is read-only
    if (yearMaintenanceDisabled && year) {
      const errorMsg = `Die Bearbeitung von Daten für Jahr ${year} ist deaktiviert. Bitte aktivieren Sie die Bearbeitung für dieses Jahr in der Jahresverwaltung.`;
      setError(errorMsg);
      setShowCleanupConfirm(false);
      console.error('Cleanup blocked - year maintenance disabled:', { year });
      return;
    }

    setCleaning(true);
    setError('');

    try {
      const response = await axios.delete(`${API_BASE_URL}/api/cleanup`);

      if (response.data.success) {
        void (response.data.deletedCount || helpers.length);
        setShowCleanupConfirm(false);
        
        // Refresh the helper list and assignments
        await fetchHelpers();
        await fetchHelperAssignments();
      } else {
        const details = response.data.details ? ` (Details: ${response.data.details})` : '';
        const helperCount = response.data.helperCount !== undefined ? ` Es waren ${response.data.helperCount} Helfer vorhanden.` : '';
        setError(`${response.data.message || 'Fehler beim Löschen'}${details}${helperCount}`);
      }
    } catch (err: any) {
      let errorMessage = 'Fehler beim Löschen der Daten';
      let details = '';

      if (err.response?.data) {
        errorMessage = err.response.data.message || errorMessage;
        if (err.response.data.details) {
          details = ` (Details: ${err.response.data.details})`;
        }
        if (err.response.data.helperCount !== undefined) {
          details += ` Es waren ${err.response.data.helperCount} Helfer vorhanden.`;
        }
      } else if (err.message) {
        details = ` (${err.message})`;
      } else if (err.code === 'ECONNREFUSED' || err.code === 'ERR_NETWORK') {
        errorMessage = 'Verbindung zum Server nicht möglich';
        details = '. Bitte stellen Sie sicher, dass der Backend-Server läuft.';
      }

      setError(`${errorMessage}${details}`);
    } finally {
      setCleaning(false);
    }
  };


  // Close menus when clicking outside (but not on the kebab buttons themselves)
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const target = event.target as HTMLElement;
      if (!target.closest('.helper-list-kebab') && !target.closest('.helper-list-kebab-menu')) {
        setOpenMenuId(null);
      }
      if (!target.closest('.helper-list-header-kebab') && !target.closest('.helper-list-header-kebab-menu')) {
        setShowHeaderMenu(false);
      }
      if (exportMenuRef.current && !exportMenuRef.current.contains(target)) {
        setShowExportMenu(false);
      }
    };
    if (openMenuId !== null || showHeaderMenu || showExportMenu) {
      // Use setTimeout to avoid immediate closure when opening
      setTimeout(() => {
        document.addEventListener('click', handleClickOutside);
      }, 0);
      return () => document.removeEventListener('click', handleClickOutside);
    }
    }, [openMenuId, showHeaderMenu, showExportMenu]);

  return (
    <div className="helper-list-overlay" onClick={onClose}>
      <div className="helper-list-container" onClick={(e) => e.stopPropagation()}>
        <div className="helper-list-close-container">
          <button className="helper-list-close" onClick={onClose}>×</button>
          {!loading && !error && helpers.length > 0 && (isAdmin() || canRegisterHelpers() || isViewer()) && (
            <div className="helper-list-export-menu-container" ref={exportMenuRef}>
              <button
                className="helper-list-export-kebab"
                onClick={(e) => {
                  e.stopPropagation();
                  setShowExportMenu(!showExportMenu);
                }}
                title="Export-Menü"
              >
                ⋮
              </button>
              {showExportMenu && (
                <div className="helper-list-export-menu">
                  {(isAdmin() || isViewer()) && (
                    <>
                      <button
                        className="helper-list-export-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          exportToExcel();
                          setShowExportMenu(false);
                        }}
                        title="Als Excel-Datei herunterladen"
                      >
                        📥 Export
                      </button>
                      <div className="helper-list-export-menu-divider"></div>
                    </>
                  )}
                  {canRegisterHelpers() && (
                    <>
                      <button
                        className="helper-list-export-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          exportToRegistrationFormat();
                          setShowExportMenu(false);
                        }}
                        title="Helfer im Registrierungsformat exportieren (für Import verwendbar)"
                      >
                        📋 Export (Registrierungsformat)
                      </button>
                      <div className="helper-list-export-menu-divider"></div>
                      <button
                        className="helper-list-export-menu-item"
                        onClick={(e) => {
                          e.stopPropagation();
                          if (!yearMaintenanceDisabled) {
                            handleImportClick();
                            setShowExportMenu(false);
                          }
                        }}
                        disabled={importing || yearMaintenanceDisabled}
                        title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt - Import nicht möglich` : "Fachbereich-Helferzuordnungen aus Excel-Datei importieren"}
                        style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                      >
                        {importing ? '⏳' : '📤'} Import Zuordnungen
                      </button>
                    </>
                  )}
                  <input
                    ref={importFileInputRef}
                    type="file"
                    accept=".xlsx,.xls"
                    onChange={handleImportAssignments}
                    style={{ display: 'none' }}
                    key="import-input-1"
                  />
                </div>
              )}
            </div>
          )}
        </div>
        <div className="helper-list-header">
          <div className="helper-list-header-top">
            <h2 className="helper-list-title">Registrierte Helfer</h2>
            {!loading && !error && helpers.length > 0 && (
              <button
                className="helper-list-header-toggle-mobile"
                onClick={() => setHeaderExpanded(!headerExpanded)}
                aria-label={headerExpanded ? "Filter ausblenden" : "Filter anzeigen"}
                title={headerExpanded ? "Filter ausblenden" : "Filter anzeigen"}
              >
                {headerExpanded ? '▼' : '▲'}
              </button>
            )}
          </div>
          {isAdmin() && adminStatusCounts && (adminStatusCounts.noFachbereich > 0 || adminStatusCounts.passtNicht > 0) && (
            <div className="helper-list-admin-focus-banner" role="status">
              <span className="helper-list-admin-focus-label">Zu bearbeiten:</span>
              {adminStatusCounts.noFachbereich > 0 && (
                <span className="helper-list-admin-focus-item">
                  <strong>{adminStatusCounts.noFachbereich}</strong> ohne Fachbereichzuordnung
                </span>
              )}
              {adminStatusCounts.noFachbereich > 0 && adminStatusCounts.passtNicht > 0 && (
                <span className="helper-list-admin-focus-sep"> · </span>
              )}
              {adminStatusCounts.passtNicht > 0 && (
                <span className="helper-list-admin-focus-item">
                  <strong>{adminStatusCounts.passtNicht}</strong> mit Passt nicht
                </span>
              )}
            </div>
          )}
          <div className="helper-list-header-actions">
            <div className="helper-list-header-left">
              {!loading && !error && helpers.length > 0 && (
                <>
                  <div className="helper-list-header-search-row">
                    <input
                      type="text"
                      id="helper-search"
                      name="helper-search"
                      className="helper-list-search-input"
                      placeholder="🔍 Suche (Vorname, Nachname, E-Mail)..."
                      value={searchQuery}
                      onChange={(e) => setSearchQuery(e.target.value)}
                      title="Nach Vorname, Nachname oder E-Mail suchen"
                    />
                    <div className="helper-list-search-select-row">
                      <select
                        id="fachbereich-filter"
                        className="helper-list-filter-select"
                        value={filterFachbereich}
                        onChange={(e) => {
                          const newFachbereich = e.target.value;
                          setFilterFachbereich(newFachbereich);
                          // If a Fachbereich is selected, automatically deselect "ohne zuordnung" filter
                          // because they are mutually exclusive (would result in empty results)
                          if (newFachbereich && showUnassignedOnly) {
                            setShowUnassignedOnly(false);
                          }
                        }}
                        title="Nach Fachbereich filtern"
                      >
                        <option value="">Alle Fachbereiche</option>
                        {fachbereiche.map((fb) => {
                          const count = getFachbereichHelperCount(fb);
                          return (
                            <option key={fb} value={fb}>
                              {fb} ({count})
                            </option>
                          );
                        })}
                      </select>
                      {/* Mobile only: button next to select. Shown via CSS. */}
                      <div className="helper-list-header-menu-inline">
                        {!isViewer() && (() => {
                          const hasVisibleEmails = filteredHelpers.filter(h => h.email && h.email.trim()).length > 0;
                          const emailOnly = !isAdmin() && canEmailVisibleHelpers() && hasVisibleEmails;
                          if (emailOnly) {
                            return (
                              <button
                                className="helper-list-header-email-button"
                                onClick={(e) => { e.stopPropagation(); setShowEmailModal(true); }}
                                title="E-Mail an alle sichtbaren Helfer senden (Filter und Suche berücksichtigt)"
                              >
                                ✉️
                              </button>
                            );
                          }
                          if (isAdmin() || (canEmailVisibleHelpers() && hasVisibleEmails)) {
                            return (
                              <>
                                <button
                                  className="helper-list-header-kebab-inline"
                                  onClick={(e) => { e.stopPropagation(); setShowHeaderMenu(!showHeaderMenu); }}
                                  title="Optionen"
                                >
                                  ⋮
                                </button>
                                {showHeaderMenu && (
                                  <div className="helper-list-header-kebab-menu">
                                    {canEmailVisibleHelpers() && hasVisibleEmails && (
                                      <>
                                        <button className="helper-list-kebab-item" onClick={(e) => { e.stopPropagation(); setShowEmailModal(true); setShowHeaderMenu(false); }} title="E-Mail an alle sichtbaren Helfer senden">✉️ E-Mail an sichtbare Helfer</button>
                                        <div className="helper-list-kebab-divider"></div>
                                      </>
                                    )}
                                    {isAdmin() && (
                                      <>
                                        <button className={`helper-list-kebab-item ${showDuplicatesOnly ? 'active' : ''}`} onClick={(e) => { e.stopPropagation(); setShowDuplicatesOnly(!showDuplicatesOnly); setShowHeaderMenu(false); }} disabled={duplicatesCount === 0} title={showDuplicatesOnly ? "Alle Helfer anzeigen" : `Nur Duplikate anzeigen (${duplicatesCount})`}>{showDuplicatesOnly ? '✓ Duplikate anzeigen' : '🔍 Duplikate suchen'} ({duplicatesCount})</button>
                                        <div className="helper-list-kebab-divider"></div>
                                      </>
                                    )}
                                    {canRegisterHelpers() && (
                                      <>
                                        <button className="helper-list-kebab-item" onClick={(e) => { e.stopPropagation(); if (!yearMaintenanceDisabled) { handleImportClick(); setTimeout(() => setShowHeaderMenu(false), 100); } }} disabled={importing || yearMaintenanceDisabled} title={yearMaintenanceDisabled ? "Jahr schreibgeschützt" : "Fachbereich-Helferzuordnungen importieren"} style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1 }}>{importing ? '⏳' : '📤'} Import Zuordnungen</button>
                                        {isAdmin() && <button className="helper-list-kebab-item" onClick={(e) => { e.stopPropagation(); exportZuordnungen(); setShowHeaderMenu(false); }} title="Fachbereich-Helferzuordnungen exportieren">📥 Export Zuordnungen</button>}
                                        <div className="helper-list-kebab-divider"></div>
                                      </>
                                    )}
                                    {isAdmin() && (
                                      <>
                                        <div className="helper-list-kebab-divider"></div>
                                        <button className="helper-list-kebab-item" onClick={(e) => { e.stopPropagation(); if (!yearMaintenanceDisabled) { setShowCleanupConfirm(true); setShowHeaderMenu(false); } }} disabled={cleaning || yearMaintenanceDisabled} title={yearMaintenanceDisabled ? "Jahr schreibgeschützt" : "Alle Helfer löschen"} style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1 }}>🛑 Alle Helfer löschen</button>
                                      </>
                                    )}
                                  </div>
                                )}
                              </>
                            );
                          }
                          return null;
                        })()}
                      </div>
                    </div>
                  </div>
                  <div className={`helper-list-header-filters ${headerExpanded ? 'expanded' : 'collapsed'}`}>
                    {/* Toggle: Ohne Zuordnung */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                        Ohne Zuordnung ({unassignedCount})
                      </span>
                      <label className={`switch-container ${unassignedCount === 0 && !showUnassignedOnly ? 'disabled' : ''}`} style={{ opacity: unassignedCount === 0 && !showUnassignedOnly ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          className="switch-input"
                          checked={showUnassignedOnly}
                          onChange={(e) => {
                            const newValue = e.target.checked;
                            setShowUnassignedOnly(newValue);
                            // If "ohne zuordnung" is selected, automatically clear Fachbereich filter
                            // because they are mutually exclusive (would result in empty results)
                            if (newValue && filterFachbereich) {
                              setFilterFachbereich('');
                            }
                          }}
                          disabled={unassignedCount === 0 && !showUnassignedOnly}
                          title={showUnassignedOnly ? "Alle Helfer anzeigen" : `Nur Helfer ohne Zuordnung anzeigen (${unassignedCount})`}
                        />
                        <span className="switch-track switch-track-red">
                          <span className="switch-thumb" />
                        </span>
                      </label>
                    </div>
                    {/* Toggle: Passt nicht */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                        Passt nicht ({passtNichtCount})
                      </span>
                      <label className={`switch-container ${passtNichtCount === 0 && !showPasstNichtOnly ? 'disabled' : ''}`} style={{ opacity: passtNichtCount === 0 && !showPasstNichtOnly ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          className="switch-input"
                          checked={showPasstNichtOnly}
                          onChange={(e) => setShowPasstNichtOnly(e.target.checked)}
                          disabled={passtNichtCount === 0 && !showPasstNichtOnly}
                          title={showPasstNichtOnly ? "Alle Helfer anzeigen" : `Nur Helfer mit 'Passt nicht' anzeigen (${passtNichtCount})`}
                        />
                        <span className="switch-track switch-track-red">
                          <span className="switch-thumb" />
                        </span>
                      </label>
                    </div>
                    {isAdmin() && showPasstNichtOnly && (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }} title={showConfirmedPasstNicht ? 'Bestätigte zentrale Bestätigungen einblenden' : 'Nur noch nicht bestätigte „Passt nicht“ anzeigen'}>
                        <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                          Bestätigte: {showConfirmedPasstNicht ? 'anzeigen' : 'ausblenden'}
                        </span>
                        <label className="switch-container">
                          <input
                            type="checkbox"
                            className="switch-input"
                            checked={showConfirmedPasstNicht}
                            onChange={(e) => setShowConfirmedPasstNicht(e.target.checked)}
                          />
                          <span className="switch-track switch-track-red">
                            <span className="switch-thumb" />
                          </span>
                        </label>
                      </div>
                    )}
                    {/* Toggle: Helfer / Kuchenspender mode */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }}>
                        {showKuchenspender ? `Kuchenspender (${kuchenCount} 🎂)` : `Helfer (${helferCount})`}
                      </span>
                      <label className={`switch-container ${helferCount === 0 && kuchenspenderCount === 0 ? 'disabled' : ''}`} style={{ opacity: helferCount === 0 && kuchenspenderCount === 0 ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          className="switch-input"
                          checked={showKuchenspender}
                          onChange={(e) => setShowKuchenspender(e.target.checked)}
                          disabled={helferCount === 0 && kuchenspenderCount === 0}
                          title={showKuchenspender ? `Zu Helfer wechseln (${helferCount})` : `Zu Kuchenspender wechseln (${kuchenspenderCount})`}
                        />
                        <span className="switch-track switch-track-red">
                          <span className="switch-thumb" />
                        </span>
                      </label>
                    </div>
                    {/* Toggle: Einzel- und Mehrfacheinsätze (off/default) vs. Helfer bereit für mehr Einsätze (n of m) (on) */}
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <span style={{ fontSize: '0.85rem', color: '#666', whiteSpace: 'nowrap' }} title={showOnlyReadyForMultiple ? 'Nur Helfer bereit für mehr Einsätze' : 'Alle Helfer (Einzel- und Mehrfacheinsätze)'}>
                        {showOnlyReadyForMultiple ? `Helfer bereit für mehr Einsätze (${readyForMultipleCount}/${totalBeforeReadyFilter})` : 'Einzel- und Mehrfacheinsätze'}
                      </span>
                      <label className={`switch-container ${totalBeforeReadyFilter === 0 && !showOnlyReadyForMultiple ? 'disabled' : ''}`} style={{ opacity: totalBeforeReadyFilter === 0 && !showOnlyReadyForMultiple ? 0.6 : 1 }}>
                        <input
                          type="checkbox"
                          className="switch-input"
                          checked={showOnlyReadyForMultiple}
                          onChange={(e) => setShowOnlyReadyForMultiple(e.target.checked)}
                          disabled={totalBeforeReadyFilter === 0 && !showOnlyReadyForMultiple}
                          title={showOnlyReadyForMultiple ? 'Alle Helfer anzeigen (Einzel- und Mehrfacheinsätze)' : `Nur Helfer bereit für mehr Einsätze anzeigen (${readyForMultipleCount}/${totalBeforeReadyFilter})`}
                        />
                        <span className="switch-track switch-track-red">
                          <span className="switch-thumb" />
                        </span>
                      </label>
                    </div>
                    {/* View switch: Zusätzliche Helferinfos (not a filter - controls display, stays in header) */}
                    <div className="helper-list-view-switch-wrap">
                      <span className="helper-list-view-switch-label">
                        Ansicht: Zusatzinfos
                      </span>
                      <label className="switch-container helper-list-view-switch">
                        <input
                          type="checkbox"
                          className="switch-input"
                          checked={showAdditionalHelperInfo}
                          onChange={(e) => setShowAdditionalHelperInfo(e.target.checked)}
                          title={showAdditionalHelperInfo ? 'Wunscheinsatzort, Einsatzzeit, Anmerkung, Aufbauhilfe ausblenden' : 'Wunscheinsatzort, Einsatzzeit, Anmerkung, Aufbauhilfe einblenden'}
                        />
                        <span className="switch-track switch-track-green">
                          <span className="switch-thumb" />
                        </span>
                      </label>
                    </div>
                  </div>
                </>
              )}
            </div>
            {/* File inputs - must be outside conditional menus to prevent unmounting */}
            <input
              ref={importFileInputRef2}
              type="file"
              accept=".xlsx,.xls"
              onChange={handleImportAssignments}
              style={{ display: 'none' }}
              key="import-input-2"
            />
          </div>
        </div>
        
        {showEmailModal && (
          <HelperListEmailModal
            onClose={() => setShowEmailModal(false)}
            bccEmails={filteredHelpers
              .filter(h => h.email && h.email.trim())
              .map(h => h.email!.trim())}
          />
        )}
        {showCleanupConfirm && (
          <div className="cleanup-confirm-overlay" onClick={() => setShowCleanupConfirm(false)}>
            <div className="cleanup-confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h3 className="cleanup-confirm-title">Alle Helfer löschen?</h3>
              {yearMaintenanceDisabled && (
                <div className="helper-list-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px', padding: '8px 12px' }}>
                  ⚠️ Das Jahr {year} ist schreibgeschützt. Alle Helfer können nicht gelöscht werden.
                </div>
              )}
              <p className="cleanup-confirm-text">
                Möchten Sie wirklich alle {helpers.length} registrierten Helfer des Jahres {year} und ihre Zuordnungen löschen?
                <br />
                <strong>Diese Aktion kann nicht rückgängig gemacht werden!</strong>
              </p>
              <div className="cleanup-confirm-actions">
                <button 
                  className="cleanup-confirm-cancel"
                  onClick={() => setShowCleanupConfirm(false)}
                  disabled={cleaning}
                >
                  Abbrechen
                </button>
                <button 
                  className="cleanup-confirm-delete"
                  onClick={handleCleanup}
                  disabled={cleaning || yearMaintenanceDisabled}
                  title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : ''}
                  style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                >
                  {cleaning ? 'Löscht...' : 'Ja, alle löschen'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showImportConfirm && pendingImportFile && (
          <div className="cleanup-confirm-overlay" onClick={handleCancelImport}>
            <div className="cleanup-confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h3 className="cleanup-confirm-title">📤 Import bestätigen</h3>
              <div className="cleanup-confirm-text" style={{ marginBottom: '20px' }}>
                <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>Datei-Informationen:</p>
                <div style={{ marginLeft: '15px', marginBottom: '10px' }}>
                  <p><strong>Dateiname:</strong> {pendingImportFile.name}</p>
                  <p><strong>Größe:</strong> {(pendingImportFile.size / 1024).toFixed(2)} KB</p>
                  <p><strong>Typ:</strong> {pendingImportFile.type || 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'}</p>
                  <p><strong>Ausgewählt am:</strong> {new Date(pendingImportFile.lastModified).toLocaleString('de-DE', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit',
                    second: '2-digit'
                  })}</p>
                </div>
                <p style={{ marginTop: '15px', color: '#666', fontSize: '0.9rem' }}>
                  Möchten Sie diese Datei jetzt importieren? Die Zuordnungen werden aktualisiert oder erstellt.
                </p>
              </div>
              <div className="cleanup-confirm-actions">
                <button 
                  className="cleanup-confirm-cancel"
                  onClick={handleCancelImport}
                  disabled={importing}
                >
                  Abbrechen
                </button>
                <button 
                  className="cleanup-confirm-delete"
                  onClick={handleConfirmImport}
                  disabled={importing}
                  style={{ backgroundColor: '#1976d2' }}
                >
                  {importing ? '⏳ Importiere...' : '✅ Importieren'}
                </button>
              </div>
            </div>
          </div>
        )}
        {deleteConfirmId && helperToDelete && (
          <div className="cleanup-confirm-overlay" onClick={() => {
            setDeleteConfirmId(null);
            setHelperToDelete(null);
            setDeleteFromAllYears(false);
          }}>
            <div className="cleanup-confirm-dialog" onClick={(e) => e.stopPropagation()}>
              <h3 className="cleanup-confirm-title">Helfer löschen?</h3>
              {yearMaintenanceDisabled && (
                <div className="helper-list-error" style={{ backgroundColor: '#e3f2fd', borderColor: '#2196f3', color: '#1565c0', marginBottom: '16px', padding: '8px 12px' }}>
                  ℹ️ Das Jahr {year} ist schreibgeschützt. Nur Löschung aus allen Jahren (DSGVO) ist möglich.
                </div>
              )}
              <p className="cleanup-confirm-text">
                Möchten Sie den Helfer <strong>{formatHelperDisplayName(helperToDelete.first_name, helperToDelete.surname)}</strong> wirklich löschen?
                <br />
                <strong>Diese Aktion kann nicht rückgängig gemacht werden!</strong>
              </p>
              {isAdmin() && (
                <label style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px', cursor: yearMaintenanceDisabled ? 'default' : 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={deleteFromAllYears}
                    onChange={(e) => !yearMaintenanceDisabled && setDeleteFromAllYears(e.target.checked)}
                    disabled={yearMaintenanceDisabled}
                  />
                  <span>Helfer aus allen Jahren löschen (DSGVO – Recht auf Löschung)</span>
                </label>
              )}
              <div className="cleanup-confirm-actions">
                <button 
                  className="cleanup-confirm-cancel"
                  onClick={() => {
                    setDeleteConfirmId(null);
                    setHelperToDelete(null);
                    setDeleteFromAllYears(false);
                  }}
                  disabled={deleting}
                >
                  Abbrechen
                </button>
                <button 
                  className="cleanup-confirm-delete"
                  onClick={() => deleteConfirmId && handleDeleteHelper(deleteConfirmId, deleteFromAllYears)}
                  disabled={deleting}
                >
                  {deleting ? 'Löscht...' : 'Ja, löschen'}
                </button>
              </div>
            </div>
          </div>
        )}
        {editingHelper && (
          <div className="cleanup-confirm-overlay" onClick={(e) => e.stopPropagation()}>
            <div className="cleanup-confirm-dialog helper-edit-dialog" onClick={(e) => e.stopPropagation()}>
              <h3 className="cleanup-confirm-title">Helfer bearbeiten</h3>
              {yearMaintenanceDisabled && (
                <div className="helper-list-error" style={{ backgroundColor: '#fff3cd', borderColor: '#ffc107', color: '#856404', marginBottom: '16px', padding: '8px 12px' }}>
                  ⚠️ Das Jahr {year} ist schreibgeschützt. Helfer können nicht bearbeitet werden. Zum Löschen (DSGVO) den Löschen-Button neben Bearbeiten verwenden.
                </div>
              )}
              {(isAdmin() || user?.role === 'Fachbereich Administrator') && !yearMaintenanceDisabled && editingHelper && (
                <div style={{ marginBottom: '16px' }}>
                  <button
                    type="button"
                    onClick={() => setShowIdentityChangeModal(true)}
                    style={{
                      padding: '8px 16px',
                      fontSize: '0.9rem',
                      border: '2px solid #ed1c24',
                      background: 'white',
                      color: '#ed1c24',
                      cursor: 'pointer',
                      fontWeight: '600'
                    }}
                  >
                    Identitätsdaten ändern (Vorname, Nachname, E-Mail)
                  </button>
                </div>
              )}
              {showIdentityChangeModal && editingHelper && token && year && (
                <HelperIdentityChangeModal
                  helper={editingHelper}
                  token={token}
                  year={year}
                  onSuccess={async (newIdentity) => {
                    setShowIdentityChangeModal(false);
                    setEditingHelper(prev => prev ? { ...prev, first_name: newIdentity.first_name, surname: newIdentity.surname, email: newIdentity.email } : null);
                    setOriginalEditFormData(prev => prev ? { ...prev, first_name: newIdentity.first_name, surname: newIdentity.surname, email: newIdentity.email } : null);
                    await fetchHelpers();
                    await fetchHelperAssignments();
                    onRefreshStatus?.();
                  }}
                  onCancel={() => setShowIdentityChangeModal(false)}
                />
              )}
              <RegistrationForm
                ref={registrationFormRef}
                initialHelper={(() => {
                  const helperData = {
                    id: editingHelper.id,
                    first_name: editingHelper.first_name,
                    surname: editingHelper.surname,
                    email: editingHelper.email || '',
                    phone: editingHelper.phone || '',
                    wunscheinsatzort: editingHelper.wunscheinsatzort || '',
                    einsatzzeit: editingHelper.einsatzzeit || '',
                    // Convert backend boolean/string to frontend 'Ja'/'Nein' string
                    // Backend returns: boolean true/false, or string 'Ja'/'Nein', or null/undefined
                    bereit_fuer_mehrere_einsaetze: (() => {
                      const bereitValue = editingHelper.bereit_fuer_mehrere_einsaetze;
                      if (bereitValue === undefined || bereitValue === null) {
                        return 'Nein';
                      }
                      // Type-safe conversion: handle boolean, string, or number
                      if (bereitValue === true || bereitValue === 'Ja' || bereitValue === '1') {
                        return 'Ja';
                      }
                      // Handle number case separately to avoid TypeScript error
                      if (typeof bereitValue === 'number' && bereitValue === 1) {
                        return 'Ja';
                      }
                      return 'Nein';
                    })(),
                    // In edit mode: ALWAYS use the actual database value (0 or 1), never default
                    // Backend always returns 0 or 1, never null/undefined, so we can safely use it directly
                    hilft_nicht_am_wettkampftag: editingHelper.hilft_nicht_am_wettkampftag !== null && editingHelper.hilft_nicht_am_wettkampftag !== undefined ? editingHelper.hilft_nicht_am_wettkampftag : 0,
                    anmerkung: editingHelper.anmerkung || '',
                    aufbauhilfe: editingHelper.aufbauhilfe || '',
                    kuchenspende: editingHelper.kuchenspende !== null && editingHelper.kuchenspende !== undefined ? editingHelper.kuchenspende : 0,
                    tshirt: editingHelper.tshirt || '',
                    fachbereiche: editingHelper.fachbereiche || [],
                    feedback: editingHelper.feedback || []
                  };
                  return helperData;
                })()}
                onSuccess={handleEditSuccess}
                onCancel={() => {
                  if (hasUnsavedEditChanges) {
                    setShowUnsavedEditConfirm(true);
                  } else {
                    handleCloseEdit();
                  }
                }}
                onDelete={() => {
                  if (!yearMaintenanceDisabled && editingHelper) {
                    setDeleteConfirmId(editingHelper.id);
                    setHelperToDelete(editingHelper);
                    setEditingHelper(null);
                  }
                }}
                onBeforeSubmit={handleBeforeSubmit}
                helperAssignments={helperAssignments}
                onFormChange={(hasChanges) => {
                  // Track changes for unsaved changes detection
                  // Note: RegistrationForm handles its own change tracking
                }}
                yearMaintenanceDisabled={yearMaintenanceDisabled}
              />
            </div>
          </div>
        )}
        {loading ? (
          <div className="helper-list-loading">Lädt...</div>
        ) : error ? (
          <div className="helper-list-error">{error}</div>
        ) : filteredHelpers.length === 0 && helpers.length > 0 ? (
          <div className="helper-list-empty">
            <div>
              {showDuplicatesOnly
                ? 'Keine Duplikate gefunden'
                : searchQuery.trim()
                  ? `Keine Helfer gefunden für "${searchQuery}"${filterFachbereich ? ` in Fachbereich "${filterFachbereich}"` : ''}`
                  : filterFachbereich
                    ? `Keine Helfer für Fachbereich "${filterFachbereich}" gefunden`
                    : showPasstNichtOnly
                      ? 'Keine Helfer mit Rückmeldung „Passt nicht“ gefunden'
                      : showUnassignedOnly
                        ? 'Keine Helfer ohne Zuordnung gefunden'
                        : showOnlyReadyForMultiple
                          ? 'Keine Helfer bereit für mehr Einsätze gefunden'
                          : 'Keine Helfer entsprechen den aktuellen Filtereinstellungen'}
            </div>
            <div style={{ marginTop: '12px', fontSize: '0.9rem', color: '#666', maxWidth: '400px' }}>
              Hinweis: Die leere Liste liegt oft an den Filtereinstellungen. Versuchen Sie, z.B. „Passt nicht", „Ohne Zuordnung" oder „Helfer bereit für mehr Einsätze" auszuschalten, einen anderen Fachbereich zu wählen oder zwischen Helfer und Kuchenspender zu wechseln.
            </div>
          </div>
        ) : helpers.length === 0 ? (
          <div className="helper-list-empty">Noch keine Helfer registriert</div>
        ) : (
          <div className="helper-list-content">
            <div className="helper-list-count" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '12px' }}>
              <span>
                {filteredHelpers.length} {filteredHelpers.length === 1 ? 'Helfer' : 'Helfer'}
                <span className="helper-list-count-total">
                  {' '}(von {helpers.length} insgesamt)
                </span>
              </span>
              {filterFachbereich && onEditFachbereich && user?.fachbereiche && user.fachbereiche.includes(filterFachbereich) && (
                <button
                  onClick={() => onEditFachbereich(filterFachbereich)}
                  style={{
                    padding: '6px 12px',
                    fontSize: '0.9rem',
                    border: '2px solid #ed1c24',
                    backgroundColor: 'white',
                    color: '#ed1c24',
                    cursor: 'pointer',
                    borderRadius: '0',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '6px',
                    transition: 'all 0.2s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = '#ed1c24';
                    e.currentTarget.style.color = 'white';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = 'white';
                    e.currentTarget.style.color = '#ed1c24';
                  }}
                  title={`Positionen zuordnen für Fachbereich "${filterFachbereich}"`}
                >
                  📍 Positionen zuordnen
                </button>
              )}
            </div>
            <div className="helper-list-items">
              {filteredHelpers.map((helper) => (
                <div key={helper.id} className={`helper-list-item ${showDuplicatesOnly ? 'helper-list-item-duplicate' : ''}`}>
                  <div className="helper-list-item-content">
                    <div className="helper-list-name">
                      <strong style={{ fontSize: '1.1rem' }}>{formatHelperDisplayName(helper.first_name, helper.surname)}</strong>
                      {helper.email && (
                        <span className="helper-list-email-wrap">
                          {' '}<a href={`mailto:${helper.email}`} className="helper-list-email-link" onClick={(e) => e.stopPropagation()}>{helper.email}</a>
                        </span>
                      )}
                      {helper.phone && <span className="helper-list-phone"> 📞 {helper.phone}</span>}
                      {/* Display "Hilft nicht am Wettkampftag" if applicable (skip when exempt - shown in feedback below) */}
                      {helper.hilft_nicht_am_wettkampftag === 1 && !isExemptFromFachbereichAssignment(helper) && (
                        <div style={{ marginTop: '8px', fontSize: '0.9rem', color: '#ed1c24', fontWeight: '500' }}>
                          ⚠️ Hilft nicht am Wettkampftag
                        </div>
                      )}
                    </div>
                    {showAdditionalHelperInfo && (helper.wunscheinsatzort || helper.einsatzzeit || helper.bereit_fuer_mehrere_einsaetze || helper.anmerkung || helper.aufbauhilfe) && (
                      <div className="helper-list-additional-info" style={{ fontSize: '0.85rem', color: '#666', marginTop: '6px' }}>
                        {(helper.wunscheinsatzort || helper.einsatzzeit || (() => {
                          const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                          return bereitValue === true || bereitValue === 'Ja' || bereitValue === '1' || (typeof bereitValue === 'number' && bereitValue === 1);
                        })()) && (
                          <div className="helper-list-additional-info-row" style={{ lineHeight: '1.4' }}>
                            <span style={{ display: 'inline' }}>
                              {helper.wunscheinsatzort && (() => {
                                const value = String(helper.wunscheinsatzort || '').trim().replace(/\s*\|\s*$/g, '');
                                return <><strong>Wunscheinsatzort:</strong> {value}</>;
                              })()}
                              {helper.wunscheinsatzort && helper.einsatzzeit && <span> | </span>}
                              {helper.einsatzzeit && (() => {
                                const value = String(helper.einsatzzeit || '').trim().replace(/\s*\|\s*$/g, '');
                                return <><strong>Einsatzzeit:</strong> {value}</>;
                              })()}
                              {(() => {
                                const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                                const isBereit = bereitValue === true || bereitValue === 'Ja' || bereitValue === '1' || (typeof bereitValue === 'number' && bereitValue === 1);
                                const showSep = (helper.wunscheinsatzort || helper.einsatzzeit) && isBereit;
                                return showSep ? <span> | </span> : null;
                              })()}
                              {(() => {
                                const bereitValue = helper.bereit_fuer_mehrere_einsaetze;
                                const isBereit = bereitValue === true || bereitValue === 'Ja' || bereitValue === '1' || (typeof bereitValue === 'number' && bereitValue === 1);
                                return isBereit ? <><strong>Ist bereit für mehrere Einsätze</strong></> : null;
                              })()}
                            </span>
                          </div>
                        )}
                        {helper.anmerkung && String(helper.anmerkung).trim() !== '' && (
                          <div className="helper-list-additional-info-row" style={{ marginTop: '6px' }}>
                            <strong>Anmerkung:</strong> {String(helper.anmerkung).trim()}
                          </div>
                        )}
                        {helper.aufbauhilfe && String(helper.aufbauhilfe).trim() !== '' && (
                          <div className="helper-list-additional-info-row" style={{ marginTop: '6px' }}>
                            <strong>Aufbauhilfe:</strong> {String(helper.aufbauhilfe).trim()}
                          </div>
                        )}
                      </div>
                    )}
                    {helper.fachbereiche && helper.fachbereiche.length > 0 && (
                      <div className="helper-list-fachbereiche">
                        Zugeordnete Fachbereiche:{' '}
                        {helper.fachbereiche.map((fachbereich, idx) => {
                          const isMyFachbereich = user?.fachbereiche && user.fachbereiche.includes(fachbereich);
                          const canOpenHelperList = isMyFachbereich && onEditFachbereich;
                          
                          if (canOpenHelperList) {
                            return (
                              <span key={idx}>
                                {idx > 0 && ', '}
                                <button
                                  type="button"
                                  onClick={(e) => {
                                    e.preventDefault();
                                    e.stopPropagation();
                                    onEditFachbereich?.(fachbereich);
                                  }}
                                  style={{
                                    background: 'none',
                                    border: 'none',
                                    padding: 0,
                                    font: 'inherit',
                                    color: '#ed1c24',
                                    textDecoration: 'underline',
                                    cursor: 'pointer'
                                  }}
                                  title={`Fachbereich "${fachbereich}" Helferliste öffnen`}
                                >
                                  {fachbereich}
                                </button>
                              </span>
                            );
                          }
                          return (
                            <span key={idx}>
                              {idx > 0 && ', '}
                              {fachbereich}
                            </span>
                          );
                        })}
                      </div>
                    )}
                    {helperAssignments[helper.id] && helperAssignments[helper.id].length > 0 && (
                      <div className="helper-list-helferpunkte">
                        <strong>Einsatzorte:</strong>

                        <table className="helper-list-helferpunkte-table">
                          <tbody>
                          {sortAssignmentsByDateAndTime(helperAssignments[helper.id]).map(hp => {
                            const weekday = getWeekdayAbbreviation(hp.einsatzdatum);
                            const timeStr = `${hp.einsatzuhrzeit}${hp.einsatzuhrzeit_bis ? ` - ${hp.einsatzuhrzeit_bis}` : ''}`;

                            return (
                              <tr key={hp.id}>
                                <td>{hp.fachbereich}</td>
                                <td>{hp.einsatzort}</td>
                                <td>{weekday ? `${weekday} ${timeStr}` : timeStr}</td>
                              </tr>
                            );
                          })}
                          </tbody>
                        </table>
                      </div>
                    )}
                    {(() => {
                      const exemptFromFachbereich = isExemptFromFachbereichAssignment(helper);

                      if (exemptFromFachbereich) {
                        return (
                          <div className="helper-list-feedback">
                            <div className="helper-list-feedback-item" style={{ color: '#666', fontStyle: 'italic', fontSize: '0.9rem' }}>
                              Hilft nicht, Fachbereich-Zuordnung nicht erforderlich
                            </div>
                          </div>
                        );
                      }

                      // Nur bei wirklich leerer Fachbereichsliste: „Noch keine Zuordnung“.
                      // Nur „Passt nicht“ auf allen Fachbereichen: weiterhin Rückmeldungen anzeigen (Filter „Ohne Zuordnung“ nutzt hasEffectiveFachbereichZuordnung separat).
                      const hasFachbereicheRaw = !!(helper.fachbereiche && helper.fachbereiche.length > 0);
                      if (!hasFachbereicheRaw) {
                        return (
                          <div className="helper-list-feedback">
                            <div className="helper-list-feedback-item" style={{ color: '#999', fontStyle: 'italic' }}>
                              Noch keine Zuordnung
                            </div>
                          </div>
                        );
                      }

                      const showKeineRelevanteZuordnung =
                        hasFachbereicheRaw && !hasEffectiveFachbereichZuordnung(helper);

                      const hasAnyRückmeldung = helper.feedback && Array.isArray(helper.feedback) &&
                        helper.feedback.some(fb => fb.rückmeldung && fb.rückmeldung.trim() !== '');

                      if (!hasAnyRückmeldung) {
                        return null;
                      }

                      // Rückmeldungen je Fachbereich (inkl. „Passt nicht“ und zentrale Bestätigung)
                      const feedbackMap = new Map<string, HelperFeedback>();
                      if (helper.feedback && Array.isArray(helper.feedback)) {
                        helper.feedback.forEach(fb => {
                          feedbackMap.set(fb.fachbereich, fb);
                        });
                      }
                      
                      return (
                      <>
                      {showKeineRelevanteZuordnung && (
                        <div className="helper-list-feedback" style={{ marginBottom: '6px' }}>
                          <div className="helper-list-feedback-item" style={{ color: '#999', fontStyle: 'italic' }}>
                            Keine relevante Zuordnung mehr
                          </div>
                        </div>
                      )}
                      <div className="helper-list-feedback">
                        <strong>Rückmeldungen:</strong>
                          {helper.fachbereiche.map((fachbereich, idx) => {
                            const fb = feedbackMap.get(fachbereich);
                            const hasRückmeldung = fb && fb.rückmeldung && fb.rückmeldung.trim() !== '';
                            const isPasstNichtFb = fb?.rückmeldung === 'Passt nicht';
                            const savingKey = `${helper.id}|${fachbereich}`;
                            const savingThis = passtNichtBestaetigungSaving === savingKey;
                            
                          return (
                            <div key={idx} className="helper-list-feedback-item">
                                <strong>{fachbereich}:</strong>
                                {hasRückmeldung ? (
                                  <>
                                    <span className="helper-list-rückmeldung">
                                      {' '}Rückmeldung: {fb.rückmeldung}
                                      {fb.bemerkung && fb.bemerkung.trim() !== '' && (
                                        <span style={{ marginLeft: '4px' }}> - Bemerkung: {fb.bemerkung}</span>
                                      )}
                                    </span>
                                    {isPasstNichtFb && fb.passtNichtZentralBestaetigtAt && (
                                      <span
                                        style={{ marginLeft: '8px', fontSize: '0.8rem', color: '#2e7d32' }}
                                        title={fb.passtNichtZentralBestaetigtAt}
                                      >
                                        Zentral bestätigt
                                      </span>
                                    )}
                                    {isAdmin() && isPasstNichtFb && (
                                      <span style={{ marginLeft: '8px', display: 'inline-flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
                                        {fb.passtNichtZentralBestaetigtAt ? (
                                          <button
                                            type="button"
                                            className="helper-list-edit-button"
                                            disabled={yearMaintenanceDisabled || savingThis}
                                            style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePasstNichtZentralBestaetigung(helper.id, fachbereich, false);
                                            }}
                                          >
                                            {savingThis ? '…' : 'Bestätigung zurücknehmen'}
                                          </button>
                                        ) : (
                                          <button
                                            type="button"
                                            className="helper-list-edit-button"
                                            disabled={yearMaintenanceDisabled || savingThis}
                                            style={{ fontSize: '0.75rem', padding: '2px 8px' }}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handlePasstNichtZentralBestaetigung(helper.id, fachbereich, true);
                                            }}
                                          >
                                            {savingThis ? '…' : 'Zentral bestätigen'}
                                          </button>
                                        )}
                                      </span>
                                    )}
                                  </>
                                ) : (
                                  <span style={{ color: '#999', fontStyle: 'italic', fontSize: '0.9rem' }}>
                                    {' '}Noch keine Rückmeldung
                                  </span>
                              )}
                            </div>
                          );
                        })}
                      </div>
                      </>
                      );
                    })()}
                  </div>
                  {(() => {
                    // Show edit button for Administrators or Fachbereich Administrators
                    // Fachbereich Administrators can only edit helpers assigned to their Fachbereiche
                    const canEdit = isAdmin() || 
                      (user?.role === 'Fachbereich Administrator' && 
                       user?.fachbereiche && 
                       user.fachbereiche.length > 0 &&
                       helper.fachbereiche && 
                       helper.fachbereiche.some(fb => user.fachbereiche!.includes(fb)));
                    
                    return canEdit ? (
                      <div className="helper-list-item-actions">
                        {yearMaintenanceDisabled && isAdmin() ? (
                          <button
                            className="helper-list-delete-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmId(helper.id);
                              setHelperToDelete(helper);
                              setDeleteFromAllYears(true);
                            }}
                            title="Helfer löschen (DSGVO – aus allen Jahren)"
                          >
                            🗑️ Löschen
                          </button>
                        ) : (
                          <button
                            className="helper-list-edit-button"
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!yearMaintenanceDisabled) handleOpenEdit(helper);
                            }}
                            disabled={yearMaintenanceDisabled}
                            title={yearMaintenanceDisabled ? `Das Jahr ${year} ist schreibgeschützt` : "Helfer bearbeiten"}
                            style={{ opacity: yearMaintenanceDisabled ? 0.6 : 1, cursor: yearMaintenanceDisabled ? 'not-allowed' : 'pointer' }}
                          >
                            ✏️ Bearbeiten
                          </button>
                        )}
                      </div>
                    ) : null;
                  })()}
                </div>
              ))}
            </div>
          </div>
        )}
        {showAssignmentWarning && editingHelper && affectedAssignments.length > 0 && (
          <div 
            className="cleanup-confirm-overlay" 
            onClick={(e) => {
              // Don't allow clicking outside to dismiss - user must choose an option
              e.stopPropagation();
            }}
            style={{ 
              zIndex: 10000,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
          >
            <div 
              className="cleanup-confirm-dialog" 
              onClick={(e) => e.stopPropagation()} 
              style={{ 
                zIndex: 10001,
                position: 'relative',
                pointerEvents: 'auto'
              }}
            >
              <h3 className="cleanup-confirm-title">Fachbereich-Zuordnung entfernen – Hinweis</h3>
              <p className="cleanup-confirm-text">
                Der Helfer <strong>{formatHelperDisplayName(editingHelper.first_name, editingHelper.surname)}</strong> hat in folgenden Fachbereichen Positionen-Zuordnungen und/oder Rückmeldungen, die entfernt werden:
              </p>
              <ul style={{ margin: '15px 0', paddingLeft: '20px', maxHeight: '300px', overflowY: 'auto' }}>
                {affectedAssignments.map((item, idx) => (
                  <li key={idx} style={{ marginBottom: '10px' }}>
                    <strong>{item.fachbereich}:</strong>
                    {item.assignments.length > 0 && (
                      <>
                        {' '}{item.assignments.length} Position{item.assignments.length !== 1 ? 'en' : ''}
                        <ul style={{ marginTop: '5px', paddingLeft: '20px', fontSize: '0.9rem', color: '#666' }}>
                          {sortAssignmentsByDateAndTime(item.assignments).map((assignment, aidx) => {
                            const weekday = getWeekdayAbbreviation(assignment.einsatzdatum);
                            const timeStr = `${assignment.einsatzuhrzeit}${assignment.einsatzuhrzeit_bis ? ` - ${assignment.einsatzuhrzeit_bis}` : ''}`;
                            const timeDisplay = weekday ? `${weekday} ${timeStr}` : timeStr;
                            return (
                              <li key={aidx}>
                                {assignment.einsatzort} ({timeDisplay})
                              </li>
                            );
                          })}
                        </ul>
                      </>
                    )}
                    {item.rückmeldung && (
                      <span style={{ display: 'block', marginTop: item.assignments.length > 0 ? '4px' : 0, fontSize: '0.9rem', color: '#666' }}>
                        Rückmeldung: {item.rückmeldung}
                      </span>
                    )}
                  </li>
                ))}
              </ul>
              <p className="cleanup-confirm-text" style={{ fontWeight: 'bold', color: '#ed1c24' }}>
                Möchten Sie fortfahren? Die Fachbereich-Zuordnung und alle Positionen-Zuordnungen werden entfernt. Rückmeldungen (auch „Passt nicht“) gehen verloren.
              </p>
              <div 
                className="cleanup-confirm-actions" 
                style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  justifyContent: 'center', 
                  flexWrap: 'wrap',
                  pointerEvents: 'auto'
                }}
              >
                <button 
                  className="cleanup-confirm-cancel"
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowAssignmentWarning(false);
                    setAffectedAssignments([]);
                    setPendingSave(null);
                  }}
                  disabled={saving}
                  style={{ pointerEvents: 'auto', cursor: saving ? 'not-allowed' : 'pointer' }}
                >
                  Abbrechen
                </button>
                <button 
                  className="cleanup-confirm-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (pendingSave) {
                      pendingSave();
                    }
                  }}
                  disabled={saving}
                  style={{ 
                    backgroundColor: '#ed1c24', 
                    color: 'white',
                    pointerEvents: 'auto', 
                    cursor: saving ? 'not-allowed' : 'pointer' 
                  }}
                >
                  {saving ? 'Wird entfernt...' : 'Fortfahren und entfernen'}
                </button>
              </div>
            </div>
          </div>
        )}
        {showUnsavedEditConfirm && editingHelper && (
          <div 
            className="cleanup-confirm-overlay" 
            onClick={(e) => {
              // Don't allow clicking outside to dismiss - user must choose an option
              e.stopPropagation();
            }}
            style={{ 
              zIndex: 10000,
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.5)'
            }}
          >
            <div 
              className="cleanup-confirm-dialog" 
              onClick={(e) => e.stopPropagation()} 
              style={{ 
                zIndex: 10001,
                position: 'relative',
                pointerEvents: 'auto'
              }}
            >
              <h3 className="cleanup-confirm-title">Ungespeicherte Änderungen</h3>
              <p className="cleanup-confirm-text">
                Sie haben ungespeicherte Änderungen am Helfer.
                <br />
                Was möchten Sie tun?
              </p>
              <div 
                className="cleanup-confirm-actions" 
                style={{ 
                  display: 'flex', 
                  gap: '10px', 
                  justifyContent: 'center', 
                  flexWrap: 'wrap',
                  pointerEvents: 'auto'
                }}
              >
                <button 
                  className="cleanup-confirm-cancel"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmDiscard();
                  }}
                  style={{ pointerEvents: 'auto', cursor: 'pointer' }}
                >
                  Abbrechen (Änderungen verwerfen)
                </button>
                <button 
                  className="cleanup-confirm-delete"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmSaveAndClose();
                  }}
                  style={{ 
                    backgroundColor: '#28a745', 
                    borderColor: '#28a745',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  Speichern
                </button>
                <button 
                  className="cleanup-confirm-cancel"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleConfirmContinue();
                  }}
                  style={{ 
                    backgroundColor: '#6c757d', 
                    borderColor: '#6c757d', 
                    color: 'white',
                    pointerEvents: 'auto',
                    cursor: 'pointer'
                  }}
                >
                  Weiter bearbeiten
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default HelperList;


